<template>
    <editor-page class="web-editor" title="Web Editor" v-if="cmsPage" no-padding v-hotkey="shortcuts">
        <template v-if="!simulatorVisible && !loading">
            <b-navbar class="editor-menu" :class="{ 'bg-white': !isTemplateMode }">
                <b-nav>
                    <b-dropdown v-if="!isTemplateMode" :size="menuButtonSize" :text="'File|Fichier' | tr"
                        variant="white" no-caret>
                        <b-dropdown-item @click="openWebsite()">
                            <i class="fas fa-folder" />
                            <span v-tr>Open Website|Ouvrir un site internet</span>
                        </b-dropdown-item>
                        <b-dropdown-item @click="save()" v-if="!isReadonly">
                            <i class="fas fa-save" />
                            <span v-tr>Save|Enregistrer</span>
                            <span class="shortcut">(Ctrl + S)</span>
                        </b-dropdown-item>
                        <b-dropdown-divider />
                        <b-dropdown-item @click="exit">
                            <i class="fas fa-right-from-bracket fa-flip-horizontal" />
                            <span v-tr>Exit|Quitter</span>
                        </b-dropdown-item>
                        <b-dropdown-divider />
                        <b-dropdown-item>
                            <i class="fas fa-info-circle" />
                            <span class="text-muted">Eye-In CMS v{{ $env.VUE_APP_PACKAGE_VERSION }}</span>
                        </b-dropdown-item>
                    </b-dropdown>

                    <b-dropdown v-if="!isTemplateMode" :size="menuButtonSize" :text="'Edit|Edition' | tr"
                        variant="white" no-caret>
                        <b-dropdown-item @click="cutBlock()" :disabled="!blockSelected">
                            <i class="fas fa-cut" />
                            <span v-tr>Cut|Couper</span>
                            <span class="shortcut">Ctrl + Shift + X</span>
                        </b-dropdown-item>
                        <b-dropdown-item @click="copyBlock()" :disabled="!blockSelected">
                            <i class="fas fa-copy" />
                            <span v-tr>Copy|Copier</span>
                            <span class="shortcut">Ctrl + Shift + C</span>
                        </b-dropdown-item>
                        <b-dropdown-item @click="duplicateBlock()" :disabled="!blockSelected">
                            <i class="fas fa-clone" />
                            <span v-tr>Duplicate|Dupliquer</span>
                            <span class="shortcut">Ctrl + Shift + D</span>
                        </b-dropdown-item>
                        <b-dropdown-item @click="pasteBlock()" :disabled="isClipboardEmpty">
                            <i class="fas fa-paste" />
                            <span v-tr>Paste|Coller</span>
                            <span class="shortcut">Ctrl + Shift + V</span>
                        </b-dropdown-item>
                        <b-dropdown-item @click="pasteBlockStyle" :disabled="isClipboardEmpty">
                            <i class="fas fa-brush" />
                            <span v-tr>Paste Style|Coller le style</span>
                            <span class="shortcut">Ctrl + Shift + Alt + V</span>
                        </b-dropdown-item>

                        <b-dropdown-divider />

                        <b-dropdown-item @click="setAddBlockMode">
                            <i class="fas fa-rectangle-history-circle-plus" />
                            <span v-tr>Add Section|Ajouter une section</span>
                            <span class="shortcut">(Ctrl + Enter)</span>
                        </b-dropdown-item>
                        <b-dropdown-item @click="openTemplatesManager">
                            <i class="fas fa-rectangle-history" />
                            <span v-tr>Manage Templates|Gérer les modèles</span>
                            <span class="shortcut">(Ctrl + Alt + T)</span>
                        </b-dropdown-item>
                        <b-dropdown-divider />
                        <b-dropdown-item @click="translationAssistant.modalVisible = true">
                            <i class="fas fa-language" />
                            <span v-tr>Translate|Traduire</span>
                        </b-dropdown-item>
                        <b-dropdown-divider />
                        <b-dropdown-item @click="$store.state.editRawHtml = !$store.state.editRawHtml">
                            <e-check-mark class="d-inline-flex ml-2 mr-1" :value="$store.state.editRawHtml" />
                            <span v-tr>Edit Raw HTML|Modifier Code HTML</span>
                        </b-dropdown-item>
                        <b-dropdown-item @click="clearAllTextFormatting">
                            <i class="fas fa-remove-format" />
                            <span v-tr>Clear All Formatting|Effacer tout le formatage</span>
                        </b-dropdown-item>
                    </b-dropdown>

                    <b-dropdown v-if="!isTemplateMode" :size="menuButtonSize" :text="'View|Affichage' | tr"
                        variant="white" no-caret>
                        <b-dropdown-item @click="changeLanguage()">
                            <i class="fas fa-flag" />
                            <span v-tr>Change UI Language|Changer la langue de l'interface</span>
                        </b-dropdown-item>
                        <b-dropdown-divider />
                        <b-dropdown-item :to="previewUrl" target="_blank">
                            <i class="fas fa-external-link" />
                            <span v-tr>Open Page in another Tab|Ouvrir la page dans un autre onglet</span>
                        </b-dropdown-item>
                        <b-dropdown-item v-if="canAdminEyeInWireless()" :to="localPreviewRoute" target="_blank">
                            <i class="fas fa-eye" />
                            <span v-tr>Open Local Preview|Ouvrir l'aperçu local</span>
                        </b-dropdown-item>
                        <b-dropdown-divider />
                        <b-dropdown-header>
                            <span v-tr>Preview|Aperçu</span>
                        </b-dropdown-header>
                        <b-dropdown-item @click="previewNavigation = !previewNavigation">
                            <e-check-mark class="d-inline-flex ml-2 mr-1" :value="previewNavigation" />
                            <span v-tr>Display Navigation|Afficher la navigation</span>
                        </b-dropdown-item>
                        <b-dropdown-item @click="highlightLinks = !highlightLinks">
                            <e-check-mark class="d-inline-flex ml-2 mr-1" :value="highlightLinks" />
                            <span v-tr>Highlight Links in text|Surligner les liens dans le texte</span>
                        </b-dropdown-item>
                        <b-dropdown-item @click="$store.state.previewDeviceFrame = !$store.state.previewDeviceFrame">
                            <e-check-mark class="d-inline-flex ml-2 mr-1" :value="$store.state.previewDeviceFrame" />
                            <span v-tr>Preview Device Frame|Aperçu de l'appareil</span>
                        </b-dropdown-item>
                        <b-dropdown-divider />
                        <b-dropdown-header>
                            <span v-tr>Simulator|Simulateur</span>
                        </b-dropdown-header>
                        <b-dropdown-item @click="setSimulatorMode(`mobile`)">
                            <i class="fas fa-mobile-iphone" />
                            <span v-tr>Mobile Preview|Prévisualiser sur mobile</span>
                        </b-dropdown-item>
                        <b-dropdown-item @click="setSimulatorMode(`tablet`)">
                            <i class="fas fa-tablet-screen" />
                            <span v-tr>Tablet Preview|Prévisualiser sur tablette</span>
                        </b-dropdown-item>
                        <b-dropdown-item @click="setSimulatorMode(`desktop`)">
                            <i class="fas fa-desktop" />
                            <span v-tr>Desktop Preview|Prévisualiser sur ordinateur</span>
                        </b-dropdown-item>
                        <b-dropdown-item @click="setSimulatorMode(`kiosk`)">
                            <i class="fak fa-kiosk" />
                            <span v-tr>Kiosk Preview|Prévisualiser sur kiosque</span>
                        </b-dropdown-item>
                        <b-dropdown-item @click="setSimulatorMode(`fullscreen`)">
                            <i class="fas fa-maximize" />
                            <span v-tr>Fullscreen Preview|Prévisualiser en plein écran</span>
                        </b-dropdown-item>
                    </b-dropdown>

                    <b-dropdown v-if="isExtraLargeLayout && !isTemplateMode" :size="menuButtonSize" class="ml-2"
                        variant="light" no-caret>
                        <template #button-content>
                            <i class="fas fa-plus" />
                            <span class="ml-2" v-if="isExtraLargeLayout" v-tr>Add|Ajouter</span>
                        </template>

                        <b-dropdown-item @click="showIframeDialog()">
                            <i class="fas fa-file-plus" />
                            <span v-tr>Create a new page|Créer une nouvelle page</span>
                        </b-dropdown-item>
                        <b-dropdown-item @click="setAddBlockMode">
                            <i class="fas fa-rectangle-history-circle-plus" />
                            <span v-tr>Add Section|Ajouter une section</span>
                            <span class="shortcut">(Ctrl + Enter)</span>
                        </b-dropdown-item>
                    </b-dropdown>

                    <b-btn v-if="isTemplateMode" variant="black" :size="menuButtonSize" class="ml-2 mr-2"
                        @click="cancelTemplateEditor">
                        <i class="fas fa-arrow-left mr-1" />
                        <span v-tr>Exit Template Editor|Quitter l'éditeur de modèles</span>
                    </b-btn>

                    <b-btn class="ml-2" @click="isTemplateMode ? saveTemplateEditor() : save()" variant="primary"
                        :size="menuButtonSize" :title="tr(`Save|Enregistrer`)" v-if="!isReadonly">
                        <i class="fas fa-save" />
                        <span class="ml-1" v-if="isExtraLargeLayout" v-tr>Save|Enregistrer</span>
                    </b-btn>
                    <b-badge v-else variant="warning" class="md" :title="tr(`Read only|Lecture seule`)">
                        <i class="fas fa-eye" />
                        <span class="ml-1" v-if="isExtraLargeLayout" v-tr>Read only|Lecture seule</span>
                    </b-badge>

                    <b-dropdown :size="menuButtonSize" v-if="!isClipboardEmpty && !isTemplateMode" variant="white"
                        no-caret>
                        <template #button-content>
                            <i class="fas fa-clipboard fa-fw" :title="tr(`Clipboard|Presse-papier`)" />
                        </template>
                        <b-dropdown-header>
                            <span v-tr>Clipboard content|Contenu du presse-papier</span>
                        </b-dropdown-header>
                        <b-dropdown-text>
                            {{ clipboardBlock.block.displayName }}
                            <div class="f-500 p5 mt-2">
                                <label>
                                    <i class="fas" :class="clipboardBlock.block.typeDef.glyph" />
                                    {{ clipboardBlock.block.typeDef.name | tr }}
                                </label>
                            </div>
                        </b-dropdown-text>
                        <!--                        <b-dropdown-item>-->
                        <!--                            <i class="fas" :class="clipboardBlock.block.typeDef.glyph"/>-->
                        <!--                            {{clipboardBlock.block.typeDef.name|tr}}-->
                        <!--                        </b-dropdown-item>-->
                        <b-dropdown-divider />
                        <b-dropdown-item @click="pasteBlock">
                            <i class="fas fa-paste" />
                            <span v-tr>Paste|Coller</span>
                        </b-dropdown-item>
                        <b-dropdown-item v-if="blockSelected && blockSelected.typeDef.isParent"
                            @click="pasteBlockAsChild" :disabled="isClipboardEmpty">
                            <i class="fak fa-solid-clipboard-circle-arrow-down" />
                            <span v-tr>Paste Inside|Coller dedans</span>
                            <span class="shortcut">Ctrl + Alt + Shift + V</span>
                        </b-dropdown-item>
                        <b-dropdown-item @click="pasteBlock" :disabled="!blockSelected">
                            <i class="fas fa-brush" />
                            <span v-tr>Paste Style|Coller style</span>
                        </b-dropdown-item>
                    </b-dropdown>
                </b-nav>

                <b-nav class="mx-auto">
                    <b-btn-group class="mr-4" v-if="pageChecker && !isTemplateMode">
                        <b-dropdown :size="menuButtonSize" :variant="pageChecker.status.variant"
                            :title="pageChecker.status.label">
                            <template #button-content>
                                <i class="fas" :class="pageChecker.status.glyph" />
                                {{ pageChecker.stats.errors || pageChecker.stats.warnings }}
                            </template>

                            <b-dropdown-header><span v-tr>AI Assistant|Assistant IA</span></b-dropdown-header>

                            <b-dropdown-item @click="setModeSEO()">
                                <i class="fas fa-globe-pointer" />
                                <span v-tr>Open SEO assistant|Ouvrir l'assistant SEO</span>
                            </b-dropdown-item>

                            <b-dropdown-divider />
                            <b-dropdown-header v-tr>Page Checker</b-dropdown-header>
                            <b-dropdown-item @click="updateReview(true)">
                                <i class="fas fa-sync" />
                                <span v-tr>Update Review|Mettre à jour la révision</span>
                            </b-dropdown-item>
                            <b-dropdown-item @click.stop.prevent="reviewVisible = true"
                                :to="`/cms/pageChecker/website/${cmsWebsite.id}/${cmsPage.id}`" target="_blank">
                                <i class="fas fa-clipboard-check" />
                                <span v-tr>Open Revision Report|Ouvrir le rapport de révision</span>
                            </b-dropdown-item>

                            <b-dropdown-item
                                @click="$store.state.display.pageChecker = !$store.state.display.pageChecker">
                                <e-check-mark class="d-inline-flex ml-2 mr-1"
                                    :value="$store.state.display.pageChecker" />
                                <span v-tr>Display Review|Afficher la révision</span>
                            </b-dropdown-item>

                            <b-dropdown-divider />

                            <b-dropdown-header v-tr>Revision Assistant|Assistant de révision</b-dropdown-header>
                            <b-dropdown-item link-class="text-danger">
                                <i class="fas fa-circle-xmark" />
                                {{ pageChecker.stats.errors }} errors
                            </b-dropdown-item>
                            <b-dropdown-item link-class="text-warning">
                                <i class="fas fa-circle-exclamation" />
                                {{ pageChecker.stats.warnings }} warnings
                            </b-dropdown-item>

                            <b-dropdown-divider />
                            <b-dropdown-item v-for="(error, index) in pageChecker.pageErrors"
                                :key="`page-error-${index}`" :link-class="`text-${error.variant}`">
                                <i class="fas" :class="error.glyph" />
                                {{ error.message }}
                            </b-dropdown-item>
                        </b-dropdown>

                        <!--                        <b-dropdown v-if="$store.state.comparison.enabled">-->
                        <!--                            <template #button-content>-->
                        <!--                                Comparison Mode-->
                        <!--                            </template>-->
                        <!--                            <div class="px-2">-->
                        <!--                                <b-form-group :label="tr(`Display|Afficher`)">-->
                        <!--                                    <b-select v-model="$store.state.comparison.display">-->
                        <!--                                        <option value="before">Before</option>-->
                        <!--                                        <option value="compare">Comparison</option>-->
                        <!--                                        <option value="after">After</option>-->
                        <!--                                    </b-select>-->
                        <!--                                </b-form-group>-->
                        <!--                            </div>-->
                        <!--                            <b-dropdown-divider/>-->
                        <!--                            <b-dropdown-item @click="applyAllChanges">-->
                        <!--                                <i class="fas fa-right-left"/>-->
                        <!--                                <span v-tr>Apply all changes|Appliquer tous les changements</span>-->
                        <!--                            </b-dropdown-item>-->
                        <!--                        </b-dropdown>-->
                    </b-btn-group>

                    <b-btn-group v-if="!isTemplateMode" class="bg-light rounded d-inline-flex align-items-center">
                        <b-dropdown :size="menuButtonSize" id="website-menu" variant="light" no-caret>
                            <template #button-content>
                                <b-img width="24" height="24" style="margin: -4px 0 -2px -2px;"
                                    v-if="cmsWebsite.design.favicon.src" :src="cmsWebsite.design.favicon.src" />
                                <i v-else class="fas fa-globe" />
                                <span class="ml-2" v-if="isExtraLargeLayout">{{ cmsWebsite.name | tr }}</span>
                            </template>
                            <b-dropdown-header><span v-tr>Website|Site internet</span></b-dropdown-header>
                            <b-dropdown-item @click="mode = MODES.THEME"
                                :disabled="!canWrite(Permission.CMS.feature.WEBSITES)">
                                <i class="fas fa-pen-paintbrush" />
                                <span v-tr>Site Design|Style du site</span>
                            </b-dropdown-item>
                            <b-dropdown-item @click="mode = MODES.NAVIGATION">
                                <i class="fas fa-browser" />
                                <span v-tr>Navigation Links|Navigation et liens</span>
                            </b-dropdown-item>

                            <template v-if="canWrite(Permission.CMS.feature.WEBSITES)">
                                <b-dropdown-divider />
                                <b-dropdown-item @click="websiteSettingsVisible = true">
                                    <i class="fas fa-cog" />
                                    <span v-tr>Website Settings & SEO|Paramètres du site et SEO</span>
                                </b-dropdown-item>
                            </template>

                            <b-dropdown-divider />

                            <b-dropdown-item @click="openWebsite()">
                                <i class="fas fa-folder" />
                                <span v-tr>Open Website|Ouvrir un site internet</span>
                            </b-dropdown-item>
                        </b-dropdown>

                        <b-popover :size="menuButtonSize" variant="primary"
                            v-if="!cmsWebsite.settings.init && cmsWebsite.design.init && cmsPage.blocks.length > 0 && !isTemplateMode && canWrite(Permission.CMS.feature.WEBSITES)"
                            target="#website-menu" placement="bottom" show>
                            <template #title>
                                <virtual-assistant-avatar />
                                <span v-tr>Change Settings|Modifier les paramètres</span>
                            </template>
                            <div class="f-500 f-12" v-tr>
                                Change site title, favicon, SEO, copyright and terms and conditions|
                                Changez le titre du site, le favicon, le SEO, les copyright et les termes et conditions
                            </div>
                            <div class="text-right mt-2">
                                <b-btn variant="white" @click="cmsWebsite.settings.init = true">
                                    <span v-tr>Dismiss|Plus tard</span>
                                </b-btn>
                                <!--                                <b-btn variant="primary" class="ml-2" @click="websiteSettingsVisible = true">-->
                                <!--                                    <span v-tr>Open Settings|Ouvrir les paramètres</span>-->
                                <!--                                </b-btn>-->
                            </div>
                        </b-popover>

                        <b-dropdown v-if="!isTemplateMode" :size="menuButtonSize" variant="light" class="border-left e-page-dropdown"
                            no-caret @hidden="groupedPagesSubmenuVisible = null" :title="cmsPage.name | tr">
                            <template #button-content>
                                <i v-if="isExtraLargeLayout" class="fas" :class="cmsPage.glyph || 'fa-file-lines'" />
                                <span class="ml-2">{{ cmsPage.name | tr }}</span>
                            </template>
                            <b-dropdown-header><span v-tr>Current Page|Page actuelle</span></b-dropdown-header>
                            <b-dropdown-item @click="openPageSettings()">
                                <i class="fas fa-cog" />
                                <span v-tr>Page Settings|Paramètres de la page</span>
                            </b-dropdown-item>
                            <e-button-confirm @confirm="duplicatePage" dropdown-item
                                :confirm-text="'You will duplicate this page|Vous allez dupliquer cette page' | tr">
                                <i class="fas fa-copy mr-1" />
                                <span v-tr>Duplicate Page|Dupliquer la page</span>
                            </e-button-confirm>
                            <e-button-confirm @confirm="deletePage" dropdown-item dropdown-class="text-danger"
                                :confirm-text="'Are you sure you want to delete this page? | Êtes-vous sûr de vouloir supprimer cette page ?' | tr">
                                <i class="fas fa-trash" />
                                <span v-tr>Delete Page|Supprimer la page</span>
                            </e-button-confirm>
                            <b-dropdown-divider />

                            <b-dropdown-item @click="showIframeDialog()">
                                <i class="fas fa-file-plus" />
                                <span v-tr>Create a new page|Créer une nouvelle page</span>
                            </b-dropdown-item>
                            <b-dropdown-divider />

                            <template v-if="homePages.length">
                                <b-dropdown-header>
                                    <span v-tr>Home Page|Page d'accueil</span>
                                </b-dropdown-header>
                                <b-dropdown-item v-for="page in homePages" :key="`page-list-${page.id}`" @click="openPage(page)">
                                    <i class="fas" :class="page.glyph ? page.glyph : 'fa-file'" />
                                    <span class="mr-2 dropdown-submenu-menu-item-label" :title="page.name | tr">{{ page.name | tr }}</span>
                                    <b-badge :variant="page.status === 'published' ? 'success' : 'light'"
                                        class="ml-auto text-capitalize">
                                        {{ page.status }}
                                    </b-badge>
                                </b-dropdown-item>
                                <b-dropdown-divider v-if="groupedPages.length" />
                            </template>

                            <div v-for="(group, gi) in groupedPages" :key="`group-${group.key}`" class="dropdown-submenu" :class="{ 'is-open': groupedPagesSubmenuVisible === group.key }"
                                @mouseenter="showGroupedPagesSubmenu(group.key)" @mouseleave="hideGroupedPagesSubmenu(group.key)">

                                <button type="button" class="dropdown-item dropdown-submenu-toggle" @click.stop.prevent="toggleGroupedPagesSubmenu(group.key)">
                                    <span class="px-2">{{ group.label }}</span>
                                    <i class="fas fa-chevron-right ml-auto" />
                                </button>

                                <div v-if="groupedPagesSubmenuVisible === group.key" class="dropdown-submenu-menu" :class="{ 'is-open': groupedPagesSubmenuVisible === group.key }">
                                    <b-dropdown-item v-for="page in group.pages" :key="`page-list-${page.id}`" @click="openPage(page)">
                                        <i class="fas" :class="page.glyph ? page.glyph : 'fa-file'" />
                                        <span class="mr-2 dropdown-submenu-menu-item-label" :title="page.name | tr">{{ page.name | tr }}</span>
                                        <b-badge :variant="page.status === 'published' ? 'success' : 'light'" class="ml-auto text-capitalize">{{ page.status }}</b-badge>
                                    </b-dropdown-item>
                                </div>

                                <b-dropdown-divider v-if="gi < groupedPages.length - 1" class="my-1" />
                            </div>
                        </b-dropdown>

                        <b-dropdown v-if="!isTemplateMode" :size="menuButtonSize" class="ml-0 border-left"
                            :variant="cmsPage.status === 'published' ? 'success' : (cmsPage.status === 'pending' ? 'warning' : 'light')"
                            no-caret>
                            <template #button-content>
                                <template v-if="isExtraLargeLayout">
                                    <i v-if="cmsPage.status === 'published'" class="fas fa-circle-check mr-2" />
                                    <i v-else-if="cmsPage.status === 'draft'" class="fas fa-file-pen mr-2" />
                                    <i v-else-if="cmsPage.status === 'pending'" class="fas fa-clock mr-2" />
                                    <i v-else class="fas fa-box-archive mr-2" />
                                </template>

                                <span class="text-capitalize">{{ cmsPage.status }}</span>
                                <!--                                <i class="fas fa-caret-down ml-2"/>-->
                            </template>

                            <b-dropdown-item value="draft" @click="draftPage">
                                <i class="fas fa-file-pen" />
                                <span v-tr>Draft|Brouillon</span>
                                <span class="shortcut">(Ctrl + Alt + Shift + D)</span>
                            </b-dropdown-item>

                            <b-dropdown-item value="pending" @click="pendingPage">
                                <i class="fas fa-clock" />
                                <span v-tr>Pending|En attente</span>
                            </b-dropdown-item>

                            <b-dropdown-item value="published" @click="publishPage">
                                <i class="fas fa-circle-check" />
                                <span v-tr>Published|Publié</span>
                                <span class="shortcut">(Ctrl + Alt + Shift + P)</span>
                            </b-dropdown-item>

                            <b-dropdown-divider />

                            <b-dropdown-item value="archive" @click="archivePage">
                                <i class="fas fa-box-archive" />
                                <span v-tr>Archive|Archivé</span>
                                <span class="shortcut">(Ctrl + Alt + Shift + A)</span>
                            </b-dropdown-item>
                        </b-dropdown>
                    </b-btn-group>

                    <div v-if="isTemplateMode" class="d-flex align-items-baseline">
                        <div class="d-flex align-items-baseline border-right pr-2 mr-2">
                            <i class="fas fa-layer-group mr-2" />
                            <span class="f-500" v-tr>Edit Template|Modifier le modèle</span>
                        </div>
                        <b-dropdown :size="menuButtonSize" variant="light" no-caret>
                            <template #button-content>
                                <span>{{ templateEditor.template?.name | tr }}</span>
                            </template>
                            <b-dropdown-header><span v-tr>Template|Modèle</span></b-dropdown-header>
                            <b-dropdown-item @click="editBlockDetails">
                                <i class="fas fa-pen mr-1" />
                                <span v-tr>Edit Details|Modifier les détails</span>
                            </b-dropdown-item>
                            <b-dropdown-item @click="duplicateTemplateBlock" dropdown-item>
                                <i class="fas fa-copy mr-1" />
                                <span v-tr>Duplicate Template|Dupliquer le modèle</span>
                            </b-dropdown-item>
                            <b-dropdown-divider />
                            <e-button-confirm @confirm="deleteTemplate(templateEditor?.template?.id)" dropdown-item
                                dropdown-class="text-danger"
                                :confirm-text="'You will delete this template block|Vous allez supprimer ce bloc modèle' | tr"
                                confirm-button-class="btn-danger">
                                <i class="fas fa-trash mr-1" />
                                <span v-tr>Delete Template|Supprimer le modèle</span>
                            </e-button-confirm>
                        </b-dropdown>
                    </div>

                    <b-btn-group class="ml-4" v-if="!$store.state.comparison.enabled">
                        <e-btn-language :size="menuButtonSize" variant="light" @input="changePreviewLanguage"
                            :value="displayLanguage" show-label />

                        <b-dropdown :size="menuButtonSize" variant="light" class="border-left"
                            :title="tr(`Translation Tools|Outils de traduction`)" v-b-tooltip>
                            <template #button-content>
                                <i class="fas fa-language" />
                            </template>

                            <b-dropdown-item :to="`/cms/editor/website/${cmsWebsite.id}/translations/pages/${cmsPage.id}`">
                                <i class="fas fa-language" />
                                <span v-tr>Translation Manager|Gestionnaire de traductions</span>
                            </b-dropdown-item>

                            <b-dropdown-item @click="translationAssistant.modalVisible = true">
                                <i class="fak fa-solid-language-rotate-right" />
                                <span v-tr>Translate|Traduire</span>
                            </b-dropdown-item>

                            <b-dropdown-divider />

                            <b-dropdown-item @click="swapLanguageTexts()">
                                <i class="fas fa-shuffle" />
                                <span v-tr>Swap Translation Texts|Échanger la traduction des textes</span>
                            </b-dropdown-item>

                            <b-dropdown-divider />

                            <b-dropdown-item class="text-danger" @click="resetLanguageTexts">
                                <i class="fas fa-text-slash" />
                                <span v-tr>Clear all texts (beta)|Supprimer tous les textes (beta)</span>
                            </b-dropdown-item>
                        </b-dropdown>
                    </b-btn-group>
                </b-nav>

                <b-dropdown :size="menuButtonSize" class="ml-2" variant="white" right>
                    <template #button-content>
                        {{ $store.state.zoom }}%
                    </template>
                    <b-dropdown-item class="pl-3" @click="setZoom(25)">25%</b-dropdown-item>
                    <b-dropdown-item class="pl-3" @click="setZoom(30)">30%</b-dropdown-item>
                    <b-dropdown-item class="pl-3" @click="setZoom(40)">40%</b-dropdown-item>
                    <b-dropdown-item class="pl-3" @click="setZoom(50)">50%</b-dropdown-item>
                    <b-dropdown-item class="pl-3" @click="setZoom(60)">60%</b-dropdown-item>
                    <b-dropdown-item class="pl-3" @click="setZoom(70)">70%</b-dropdown-item>
                    <b-dropdown-item class="pl-3" @click="setZoom(80)">80%</b-dropdown-item>
                    <b-dropdown-item class="pl-3" @click="setZoom(90)">90%</b-dropdown-item>
                    <b-dropdown-item class="pl-3" @click="setZoom(100)">100%</b-dropdown-item>
                    <b-dropdown-item class="pl-3" @click="setZoom(110)">110%</b-dropdown-item>
                    <b-dropdown-item class="pl-3" @click="setZoom(125)">125%</b-dropdown-item>
                    <b-dropdown-item class="pl-3" @click="setZoom(150)">150%</b-dropdown-item>
                </b-dropdown>

                <b-dropdown :size="menuButtonSize" variant="white" right>
                    <template #button-content>
                        <template v-if="previewMode === `mobile`">
                            <i class="fas fa-mobile-iphone" />
                            <span class="ml-1" v-if="isExtraLargeLayout" v-tr>Mobile</span>
                        </template>
                        <template v-else-if="previewMode === `tablet`">
                            <i class="fas fa-tablet-screen" />
                            <span class="ml-1" v-if="isExtraLargeLayout" v-tr>Tablet|Tablette</span>
                        </template>
                        <template v-else-if="previewMode === `desktop`">
                            <i class="fas fa-desktop" />
                            <span class="ml-1" v-if="isExtraLargeLayout" v-tr>Desktop|Ordinateur</span>
                        </template>
                        <template v-else-if="previewMode === `kiosk`">
                            <i class="fak fa-kiosk" />
                            <span class="ml-1" v-if="isExtraLargeLayout" v-tr>Kiosk|Kiosque</span>
                        </template>
                    </template>
                    <b-dropdown-item @click="setPreviewMode(`desktop`)"
                        :class="{ 'active': previewMode === `desktop` }">
                        <i class="fas fa-desktop" />
                        <span v-tr>Desktop Display|Affichage ordinateur</span>
                    </b-dropdown-item>
                    <b-dropdown-divider />
                    <b-dropdown-item @click="setPreviewMode(`mobile`)" :class="{ 'active': previewMode === `mobile` }">
                        <i class="fas fa-mobile-iphone" />
                        <span v-tr>Mobile Display|Affichage mobile</span>
                    </b-dropdown-item>
                    <b-dropdown-item @click="setPreviewMode(`tablet`)" :class="{ 'active': previewMode === `tablet` }">
                        <i class="fas fa-tablet-screen" />
                        <span v-tr>Tablet Display|Affichage tablette</span>
                    </b-dropdown-item>
                    <b-dropdown-item @click="setPreviewMode(`kiosk`)" :class="{ 'active': previewMode === `kiosk` }">
                        <i class="fak fa-kiosk" />
                        <span v-tr>Kiosk Display|Affichage kiosque</span>
                    </b-dropdown-item>
                </b-dropdown>

                <template v-if="!isTemplateMode">
                    <b-dropdown v-if="canAdminEyeInWireless()" size="sm" variant="light" right
                        @click="openPreview" split>
                        <template #button-content>
                            <i class="fas fa-external-link" />
                            <span class="ml-1" v-if="isExtraLargeLayout" v-tr>Open|Ouvrir</span>
                        </template>
                        <b-dropdown-item :to="localPreviewRoute" target="_blank">
                            <i class="fas fa-eye" />
                            <span class="ml-1" v-if="isExtraLargeLayout" v-tr>Open Local Preview|Ouvrir l'aperçu local</span>
                        </b-dropdown-item>
                    </b-dropdown>
                    <b-btn v-else :size="menuButtonSize" variant="light" :href="previewUrl" target="_blank"
                        :title="'Open in external window|Ouvrir dans une autre fenêtre' | tr">
                        <i class="fas fa-external-link" />
                        <span class="ml-1" v-if="isExtraLargeLayout" v-tr>Open|Ouvrir</span>
                    </b-btn>
                </template>
            </b-navbar>

            <editor-submenu :disabled="!blockSelected" />

            <div class="editor-container horizontal">
                <div class="padding-bar vertical">
                    <template v-if="blockSelected">
                        <b-btn variant="light" size="xs" class="toggle-advanced-padding"
                            @click="advancedPadding = !advancedPadding"
                            :title="tr(`Toggle advanced padding|Padding avancé`)">
                            <i class="fas" :class="advancedPadding ? 'fa-link-slash' : 'fa-link'" />
                        </b-btn>
                        <padding-rule direction="top" v-model="blockSelected.padding.top"
                            @input="!advancedPadding ? blockSelected.padding.bottom = $event : null" />
                        <padding-rule v-if="advancedPadding" class="mt-auto" direction="bottom"
                            v-model="blockSelected.padding.bottom" />
                    </template>
                </div>
                <template v-if="!isTemplateMode && cmsPage.blocks.length === 0">
                    <e-empty-placeholder title="This page looks empty|Cette page a l'air un peu vide">
                        <b-btn id="add-section" variant="primary" @click="setAddBlockMode">
                            <span v-tr>Add your First Section|Ajouter votre première section</span>
                        </b-btn>
                    </e-empty-placeholder>

                    <b-popover variant="primary" v-if="cmsPage.blocks.length === 0" target="#add-section"
                        placement="bottom" show>
                        <template #title>
                            <virtual-assistant-avatar />
                            <span v-tr>This page looks empty|Cette page est vide</span>
                        </template>
                        <div class="f-500 f-12" v-tr>
                            Start by adding content to your page by clicking on the button above.|Commencez en ajoutant
                            du contenu dans votre page en cliquant sur le bouton ci-dessus.
                        </div>
                    </b-popover>
                </template>
                <preview-responsive-container
                    v-else-if="(cmsPage.blocks.length > 0 && !isTemplateMode) || (isTemplateMode && templateEditor.template)"
                    :class="{ 'background-blueprint': isTemplateMode, 'highlight-links': highlightLinks }">
                    <template #above>
                        <div class="padding-bar horizontal">
                            <cms-container v-if="blockSelected" :mode="blockSelected.container" horizontal
                                style="background-color: transparent">
                                <i class="toggle-container left fas" @click="toggleBlockContainer" :class="{
                                    'fa-chevron-right': blockSelected.container === 'fill',
                                    'fa-chevron-left': blockSelected.container === 'contained',
                                    'fa-chevrons-left': blockSelected.container === 'extended'
                                }" />
                                <padding-rule direction="left" v-model="blockSelected.padding.left"
                                    @input="!advancedPadding ? blockSelected.padding.right = $event : null"
                                    :max="isMobileLayoutOrPreview ? 150 : cmsWebsite.body.design.style.maxWidth / 2 - 50" />
                                <padding-rule v-if="advancedPadding" direction="right"
                                    v-model="blockSelected.padding.right" class="ml-auto"
                                    :max="isMobileLayoutOrPreview ? 150 : cmsWebsite.body.design.style.maxWidth / 2 - 50" />
                            </cms-container>
                        </div>
                    </template>
                    <cms-block-builder :blocks="cmsPage.blocks" v-model="blockSelected" :key="$store.state.cache"
                        :class="{ 'p-5': isTemplateMode }" :no-navigation="!isNavigationVisible"
                        :block-selection="blockSelection" :clipboard="clipboard" @block-selected="selectBlock"
                        editable />

                    <div v-if="isTemplateMode" class="template-mode-toast">
                        <div class="toast-box">
                            <svg class="progress-ring" viewBox="0 0 200 60" preserveAspectRatio="none"
                                aria-hidden="true">
                                <!-- inset by 1px so the 2px stroke sits inside the 200×60 box -->
                                <rect x="1" y="1" width="198" height="58" rx="29" ry="29" pathLength="100"
                                    vector-effect="non-scaling-stroke" />
                            </svg>

                            <div class="toast-content">
                                <span v-tr>Template Mode|Mode modèle</span>
                            </div>
                        </div>
                    </div>
                </preview-responsive-container>
            </div>
        </template>
        <template v-else-if="simulatorVisible">
            <b-navbar class="editor-menu bg-white">
                <h3 class="m-0" v-tr>Device Simulator|Simuler un appareil</h3>
                <b-btn class="ml-auto" variant="light-primary" @click="leaveSimulatorMode()">
                    <i class="fas fa-eye-slash mr-1" />
                    <span v-tr>Quit Simulator|Quitter le simulateur</span>
                </b-btn>
            </b-navbar>
            <e-tv-preview view="portrait" iframe-mode v-if="simulatorMode === 'kiosk'"
                style="margin-top: -55px; width: 1080px; height: calc(100vh - 70px);">
                <template #header>
                    <i class="fas fa-globe mr-1" />
                    {{ cmsWebsite.domain }}
                </template>
                <iframe :src="`${previewUrl}?display=kiosk&embed`" style="width: 1080px; height: calc(100vh - 70px)" />
            </e-tv-preview>
            <e-responsive-preview v-else iframe-mode :default-view="simulatorMode"
                :class="{ 'mt-2 mt-lg-4': simulatorMode !== 'desktop' }">
                <template #header>
                    <i class="fas fa-globe mr-1" />
                    {{ cmsWebsite.domain }}
                </template>
                <iframe :src="`${previewUrl}?embed`" />
            </e-responsive-preview>
        </template>

        <b-modal v-if="pageSettings.modalVisible" v-model="pageSettings.modalVisible"
            :title="'Page Settings|Paramètres de la page' | tr" body-bg-variant="white" size="lg" scrollable
            :ok-title="'Save|Enregistrer' | tr" @ok="onPageEdited()">
            <page-settings-modal v-model="cmsPage" v-if="pageSettings.modalVisible" :tab.sync="pageSettings.tab" />
        </b-modal>


        <b-modal v-if="translationAssistant.modalVisible" v-model="translationAssistant.modalVisible" size="lg"
            body-bg-variant="white" :title="tr(`Translation Assistant|Assistant de traduction`)"
            :ok-title="'Translate|Traduire' | tr" @ok="autoTranslate(translationAssistant.replaceAll)">

            <div class="d-flex align-items-center justify-content-center p-3 w-100" style="gap:20px">
                <e-btn-language v-model="translationAssistantFromLanguage" :locales="translationAssistantLanguageCodes"
                    show-label size="lg" variant="light" />
                <i class="fa-solid fa-arrow-right fa-2x" />
                <e-btn-language v-model="translationAssistantToLanguage" :locales="translationAssistantLanguageCodes"
                    show-label size="lg" variant="light" />
            </div>

            <b-card class="mx-auto border my-3" style="max-width: 320px;">
                <h4 class="text-center mt-0 mb-2">Options</h4>
                <b-checkbox v-model="translationAssistant.fullText" switch>
                    <span class="f-500" v-tr>Use new AI Full Translation|Utiliser la nouvelle IA de traduction</span>
                </b-checkbox>

                <b-checkbox v-model="translationAssistant.replaceAll" switch>
                    <span class="f-500" v-tr>Replace All text|Remplacer tous les textes</span>
                </b-checkbox>

                <div class="form-help mt-1">
                    <span v-if="translationAssistant.replaceAll" v-tr>
                        All existing translation will be replaced|Toutes les traductions existantes seront remplacées
                    </span>
                    <span v-else v-tr>
                        Only missing translation will be replaced|Seules les traductions manquantes seront remplacées
                    </span>
                </div>
            </b-card>

            <e-translation-input v-model="cmsPage.meta.translationContext" :force-lang="translateLanguage"
                type="textarea" auto-translate
                :label="'Describe Context for better translation|Donnez du contexte pour une meilleure traduction' | tr"
                height="200px" :use-locales="cmsLocales" />

        </b-modal>

        <b-modal v-if="templateEditor.editDetailsModalVisible" v-model="templateEditor.editDetailsModalVisible"
            :title="tr(`Edit Details|Modifier les détails`)" size="lg" @ok="updateTemplateDetails">
            <div class="template-modal-content" v-if="templateEditor.editingTemplate">
                <b-form-group :label="tr(`Template Name|Nom du modèle`)">
                    <b-input v-model="templateEditor.editTemplateName"
                        :placeholder="tr(`Enter template name|Entrez le nom du modèle`)" />
                </b-form-group>

                <b-form-group :label="tr(`Description|Description`)">
                    <b-textarea v-model="templateEditor.editTemplateDescription"
                        :placeholder="tr(`Optional description|Description optionnelle`)" rows="3" />
                </b-form-group>

                <b-form-group :label="tr(`Block Preview|Aperçu du bloc`)">
                    <div class="template-large-preview p-3">
                        <block-preview :block="templateEditor.editingTemplate.block" :scale="0.6" :max-height="300" />
                    </div>
                </b-form-group>
            </div>
        </b-modal>

        <b-modal v-if="duplicateTemplateModal.visible" v-model="duplicateTemplateModal.visible" size="lg"
            :title="tr(`Duplicate Template Block|Dupliquer le bloc modèle`)" @ok="saveDuplicateTemplateBlock">
            <div class="template-modal-content" v-if="duplicateTemplateModal.blockToDuplicate">
                <b-form-group :label="tr(`Block Name|Nom du bloc`)">
                    <b-input v-model="duplicateTemplateModal.templateName"
                        :placeholder="tr(`Enter block name|Entrez le nom du bloc`)" />
                </b-form-group>

                <b-form-group :label="tr(`Description|Description`)">
                    <b-textarea v-model="duplicateTemplateModal.templateDescription"
                        :placeholder="tr(`Optional description|Description optionnelle`)" rows="3" />
                </b-form-group>

                <b-form-group :label="tr(`Block Preview|Aperçu du bloc`)">
                    <div class="template-large-preview">
                        <block-preview :block="duplicateTemplateModal.blockToDuplicate" :scale="0.6" />
                    </div>
                </b-form-group>
            </div>
        </b-modal>

        <b-modal v-if="templateSelectionModal.visible" v-model="templateSelectionModal.visible"
            :title="tr(`Manage Your Templates|Gérer vos modèles`)" size="lg" scrollable body-class="p-0">
            <div v-if="templates.length === 0" class="text-center f-500 f-14 p-5">
                <span v-tr>No saved templates yet.|Aucun modèle sauvegardé pour le moment.</span>
            </div>
            <div v-else class="template-selection-modal-grid p-4">
                <div v-for="(template, index) in templates" :key="'template-modal-' + index" class="position-relative">
                    <template-block-card class="template-item" :template="template" :preview-height="140"
                        @select="onSelectTemplateForEditing" />
                    <b-dropdown right variant="simple" size="small" no-caret class="template-item-actions">
                        <template #button-content>
                            <i class="fas fa-ellipsis-v"></i>
                        </template>
                        <b-dropdown-item variant="outline-success" @click.stop="onSelectTemplateForEditing(template)">
                            <i class="fas fa-pen mr-1" />
                            <span v-tr>Edit Template|Modifier le modèle</span>
                        </b-dropdown-item>
                        <b-dropdown-item variant="outline-success" @click="insertTemplateBlock(template)">
                            <i class="fas fa-plus mr-1" />
                            <span v-tr>Insert Template|Insérer le modèle</span>
                        </b-dropdown-item>
                        <b-dropdown-item variant="outline-primary" @click="openDuplicateTemplateModal(template)">
                            <i class="fas fa-copy mr-1" />
                            <span v-tr>Duplicate Template|Dupliquer le modèle</span>
                        </b-dropdown-item>
                        <b-dropdown-divider />
                        <b-dropdown-item variant="danger" @click="deleteTemplate(template.id)">
                            <i class="fas fa-trash mr-1" />
                            <span v-tr>Delete Template|Supprimer le modèle</span>
                        </b-dropdown-item>
                    </b-dropdown>
                </div>
            </div>
            <template #modal-footer>
                <b-btn variant="outline-secondary" @click="templateSelectionModal.visible = false">
                    <span v-tr>Cancel|Annuler</span>
                </b-btn>
            </template>
        </b-modal>

        <template v-if="!simulatorVisible && !loading" #sidebar-right>
            <properties-text-compare v-if="$store.state.comparison.enabled" />
            <template v-else>
                <properties-page v-model="cmsPage" @add-block="setAddBlockMode" @block-selected="selectBlock"
                    @edit-theme="mode = MODES.THEME" @edit-menu="selectBlock(cmsWebsite.menu.block)"
                    @edit-footer="selectBlock(cmsWebsite.footer.block)" :show-navigation="previewNavigation" />

                <template v-if="[MODES.BLOCK, MODES.TEMPLATE].includes(mode)">
                    <properties-block-renderer v-for="(block, index) in blockSelection" :key="`block-props-${block.id}`"
                        v-model="blockSelection[index]" :parent-block="index > 0 ? blockSelection[index - 1] : null"
                        :count="cmsPage.blocks.length" :block-selection="blockSelection"
                        :no-slide="index > 0 && blockSelection.length > 1"
                        :readonly="isBlockSelectedChild && blockSelected.childrenReadonly" @block-selected="selectBlock"
                        @close="selectParentBlock" @move-up="moveBlockUp" @move-down="moveBlockDown"
                        @wrap-block="openWrapBlockProperties" @replace-block="replaceBlock" @duplicate="duplicateBlock"
                        @cut-block="cutBlock" @copy-block="copyBlock" @paste-block="pasteBlock"
                        @paste-block-child="pasteBlockAsChild" @paste-block-style="pasteBlockStyle"
                        @delete="deleteBlock" @edit-navigation="mode = MODES.NAVIGATION"
                        @save-template="saveTemplate" />
                </template>
                <properties-website-style v-if="cmsWebsite.design.colorSchemes" :visible="mode === MODES.THEME"
                    v-model="cmsWebsite" @close="setWebsiteEdited"
                    @edit-website-settings="websiteSettingsVisible = true" />
                <properties-website-navigation :visible="mode === MODES.NAVIGATION" v-model="cmsWebsite"
                    @close="setWebsiteEdited" />
                <properties-select-block ref="propertiesSelectBlockRef" :visible="mode === MODES.ADD_BLOCK"
                    :templates="templates" @select-block="createBlock" @select-template="createBlockFromTemplate"
                    @delete-template="deleteTemplate" @update-template="updateTemplate"
                    @edit-template-block="openTemplateEditor" @manage-templates="openTemplatesManager"
                    @close="resetMode" />

                <properties-select-block v-if="blockSelected && mode === MODES.WRAP" :visible="mode === MODES.WRAP"
                    :block="blockSelected" parent-only title="Select Container|Choisir un conteneur"
                    @select-block="wrapBlock" @save-template="saveTemplate" @update-template="updateTemplate"
                    @edit-template-block="openTemplateEditor" @close="resetMode" />

                <properties-seo v-if="pageChecker && mode === MODES.SEO" :visible="mode === MODES.SEO"
                    @close="resetMode" @edit-page-assistant-settings="openPageSettings(1)"
                    @edit-page-seo="openPageSettings(2)" />
            </template>
        </template>

        <!-- don't use preset here because it would conflict because cms and cms-next -->
        <e-iframe-dialog v-model="pageCreationVisible"
            :src="`/cms/editor/website/${cmsWebsite.id}/pages/creation?dealerid=${cmsWebsite.dealerid}`" fill-height
            title="New Page|Nouvelle page" glyph="fas fa-file-circle-plus" @created="onPageCreated" />
        <e-iframe-dialog v-model="pageDuplicateVisible"
            :src="`/cms/editor/website/${cmsWebsite.id}/pages/creation?dealerid=${cmsWebsite.dealerid}&duplicateId=${cmsPage.id}`"
            fill-height title="Duplicate Page|Dupliquer la page" glyph="fas fa-copy" @created="onPageDuplicate" />
        <e-iframe-dialog v-model="websiteSettingsVisible" :src="`/cms/editor/website/${cmsWebsite.id}/settings`"
            fill-height title="Website Settings|Paramètres du site" glyph="fas fa-cog" @saved="websiteSettingsSaved"
            @redirect="editWebsitePage" />

        <e-iframe-dialog v-if="reviewVisible" v-model="reviewVisible"
            :src="`/cms/review/website/${cmsWebsite.id}/${cmsPage.id}`" fullscreen />

    </editor-page>
</template>

<script>
import BtnLanguage from "@/components/btn-language.vue";
import EBtnLanguage from "@vue-components/components/e-btn-language.vue";
import PageSettingsModal from "@/components/editor/page-settings-modal.vue";
import PropertiesBlockRenderer from "@/components/editor/properties/blocks/properties-block-renderer.vue";
import EditorSubmenu from "@/components/editor/editor-submenu.vue";
import PropertiesComponentNavigationLinks
    from "@/components/editor/properties/components/properties-component-navigation-links.vue";
import PaddingRule from "@/components/editor/properties/padding-rule.vue";
import PropertiesWebsiteNavigation from "@/components/editor/properties/properties-website-navigation.vue";
import PropertiesWebsiteStyle from "@/components/editor/properties/website/properties-website-style.vue";
import CmsBlockBuilder from "@/components/editor/rendering/blocks/cms-block-builder.vue";
import PropertiesSelectBlock from "@/components/editor/properties/properties-select-block.vue";
import PropertiesPage from "@/components/editor/properties/properties-page.vue";
import CmsContainer from "@/components/editor/rendering/cms-container.vue";
import VirtualAssistantAvatar from "@/components/virtual-assistant-avatar.vue";
import CmsBlock from "@/helpers/editor/blocks/CmsBlock.js";
import CmsBlockFooter from "@/helpers/editor/blocks/CmsBlockFooter.js";
import CmsBlockMenu from "@/helpers/editor/blocks/CmsBlockMenu.js";
import CmsBlockTemplate from "@/helpers/editor/templates/CmsBlockTemplate.js";
import PageChecker from "@/helpers/review/PageChecker.js";
import EditorPage from "@/layout/editor-page.vue";
import PreviewResponsiveContainer from "@/layout/preview-responsive-container.vue";
import PropertiesTextCompare from "@components/editor/properties/properties-text-compare.vue";
import PropertiesSeo from "@components/editor/properties/properties-seo.vue";
import { swal } from "vue-swal";
import EButtonConfirm from "@vue-components/components/e-button-confirm.vue";
import ECheckMark from "@vue-components/components/e-check-mark.vue";
import EEmptyPlaceholder from "@vue-components/components/e-empty-placeholder.vue";
import EIframeDialog from "@vue-components/components/e-iframe-dialog.vue";
import EResponsivePreview from "@vue-components/components/e-responsive-preview.vue";
import ETvPreview from "@vue-components/components/e-tv-preview.vue";
import { changeLanguage } from "@vue-components/helpers/i18n.js";
import { setLocale, getLanguageObject, normalizeLocaleCode, getFallbackLocale } from "@vue-components/helpers/I18nPluginHelper.js";
import NetworkV2 from "@vue-components/helpers/NetworkV2.js";
import Permission from "@vue-components/helpers/permissions";
import EStatusBadge from "@vue-components/components/e-status-badge.vue";
import EBadgeItem from "@vue-components/components/e-badge-item.vue";
import ETranslationInput from "@vue-components/components/e-translation-input.vue";
import BlockPreview from "@/components/editor/properties/components/block-preview.vue";
import TemplateBlockCard from "@/components/editor/templates/template-block-card.vue";
import ClipboardMixin from "@/helpers/clipboard/clipboard-mixin.js";

// web-editor #hot-reload-debug
export default {
    name: `web-editor`,
    mixins: [ClipboardMixin],
    components: {
        BlockPreview,
        TemplateBlockCard,
        PropertiesTextCompare,
        PropertiesSeo,
        ETranslationInput,
        EBadgeItem,
        EStatusBadge,
        EBtnLanguage,
        ECheckMark,
        ETvPreview,
        PropertiesComponentNavigationLinks,
        VirtualAssistantAvatar,
        PropertiesWebsiteNavigation,
        PropertiesWebsiteStyle,
        EEmptyPlaceholder,
        BtnLanguage,
        PageSettingsModal,
        EResponsivePreview,
        EIframeDialog,
        EditorSubmenu,
        CmsContainer,
        PaddingRule,
        PropertiesBlockRenderer,
        EButtonConfirm,
        PropertiesPage,
        PropertiesSelectBlock,
        CmsBlockBuilder,
        PreviewResponsiveContainer,
        // EResponsivePreview,
        EditorPage
    },
    data() {
        return {
            Permission,
            loading: false,
            MODES: {
                STRUCT: `struct`,
                ADD_BLOCK: `add-block`,
                BLOCK: `block`,
                NAVIGATION: `navigation`,
                THEME: `theme`,
                SEO: `seo`,
                WRAP: `wrap`,
                TEMPLATE: `template`
            },
            mode: `struct`,
            blockSelection: [], // hierarchy of blocks selected from parent to child
            websiteEdited: false,
            previewNavigation: true,
            highlightLinks: true,
            pageCreationVisible: false,
            translationAssistant: {
                modalVisible: false,
                replaceAll: false,
                fullText: true,
                fromLanguage: null,
                toLanguage: null
            },
            pageDuplicateVisible: false,
            websiteSettingsVisible: false,
            pageSettings: {
                modalVisible: false,
                tab: 0
            },
            simulatorVisible: false,
            simulatorMode: `mobile`,
            duplicateId: null,
            clipboard: null,
            advancedPadding: null,
            history: [],
            historyIndex: -1,
            reviewVisible: false,
            focusListener: null,
            groupedPagesSubmenuVisible: null,
            templates: [],
            templateEditor: {
                active: false,
                template: null,
                previous: null,
                editDetailsModalVisible: false,
                editingTemplate: null,
                editTemplateName: ``,
                editTemplateDescription: ``
            },
            duplicateTemplateModal: {
                visible: false,
                blockToDuplicate: null,
                templateName: ``,
                templateDescription: ``
            },
            templateSelectionModal: {
                visible: false
            }
        }
    },
    computed: {
        isTemplateMode() {
            return this.templateEditor.active === true;
        },
        menuButtonSize() {
            return this.isExtraLargeLayout ? `sm` : `xs`;
        },
        translateLanguage() {
            return this.translationAssistantToLanguage;
        },
        displayLanguageObject() {
            return getLanguageObject(this.translationAssistantFromLanguage);
        },
        translateLanguageObject() {
            return getLanguageObject(this.translateLanguage);
        },
        isReadonly() {
            return !this.canWrite(Permission.CMS.feature.PAGES);
        },
        translationAssistantLanguageCodes() {
            const selected = (this.cmsWebsite?.settings?.languages || [])
                .map(lang => normalizeLocaleCode(lang))
                .filter(Boolean);
            const unique = Array.from(new Set(selected));
            if (!unique.length) {
                unique.push(getFallbackLocale());
            }
            return unique;
        },
        translationAssistantLanguageOptions() {
            return this.translationAssistantLanguageCodes.map(code => {
                const lang = getLanguageObject(code);
                const name = lang?.name || code;
                return {
                    value: code,
                    text: `${name} (${String(code).toUpperCase()})`
                };
            });
        },
        translationAssistantFromLanguage: {
            get() {
                return normalizeLocaleCode(this.translationAssistant.fromLanguage) ||
                    normalizeLocaleCode(this.displayLanguage) ||
                    getFallbackLocale();
            },
            set(value) {
                const normalized = normalizeLocaleCode(value) || getFallbackLocale();
                this.translationAssistant.fromLanguage = normalized;
                this.ensureTranslationAssistantTargets();
            }
        },
        translationAssistantToLanguage: {
            get() {
                const normalized = normalizeLocaleCode(this.translationAssistant.toLanguage);
                if (normalized) {
                    return normalized;
                }
                const from = this.translationAssistantFromLanguage;
                const next = this.translationAssistantLanguageCodes.find(code => code !== from);
                return next || from || getFallbackLocale();
            },
            set(value) {
                const normalized = normalizeLocaleCode(value) || getFallbackLocale();
                this.translationAssistant.toLanguage = normalized;
            }
        },
        previewUrl() {
            return this.cmsWebsite.getPreviewUrl(this.cmsPage);
        },
        localPreviewRoute() {
            return this.cmsWebsite.getLocalPreviewRoute(this.cmsPage);
        },
        isBlockSelectionEmpty() {
            return this.blockSelection && this.blockSelection.length > 1;
        },
        isBlockSelectedChild() {
            return this.blockSelection && this.blockSelection.length > 1;
        },
        blockSelected: {
            get() {
                if (this.blockSelection.length > 0) {
                    return this.blockSelection[this.blockSelection.length - 1];
                } else {
                    return null;
                }
            },
            set(value) {
                this.selectBlock(value);
            }
        },
        blockSelectedParent() {
            if (this.blockSelection.length > 1) {
                return this.blockSelection[this.blockSelection.length - 2];
            } else {
                return null;
            }
        },
        blockSelectedRoot() {
            if (this.blockSelection.length > 0) {
                return this.blockSelection[0];
            } else {
                return null;
            }
        },
        shortcuts() {
            return {
                "ctrl+s": this.save,
                "ctrl+alt+shift+p": this.publishPage,
                "ctrl+alt+shift+d": this.draftPage,
                "ctrl+alt+shift+a": this.archivePage,
                "ctrl+enter": this.setAddBlockMode,
                "ctrl+shift+up": this.moveBlockUp,
                "ctrl+shift+down": this.moveBlockDown,
                "ctrl+shift+d": this.duplicateBlock,
                "ctrl+shift+x": this.cutBlock,
                "ctrl+shift+c": this.copyBlock,
                "ctrl+alt+v": this.pasteBlockStyle,
                "ctrl+shift+v": this.pasteBlock,
                "ctrl+alt+shift+v": this.pasteBlockAsChild,
                "ctrl+delete": this.deleteBlock,
                "shift+r": this.updateReview,
                "ctrl+z": this.undo,
                "ctrl+shift+z": this.redo,
                "ctrl+home": this.scrollToBlock,
                "ctrl+alt+t": this.openTemplatesManager,
                // delete: this.delete
            }
        },
        blockIndex() {
            if (this.blockSelectedParent) {
                return this.blockSelectedParent.children.indexOf(this.blockSelected);
            }
            return this.cmsPage.blocks.indexOf(this.blockSelected);
        },
        isNavigationVisible() {
            if (this.isTemplateMode) {
                return false;
            }
            return this.previewNavigation || this.mode === this.MODES.NAVIGATION;
        },
        groupedPages() {
            const pages = (this.cmsPages || []).slice();
            const map = new Map();
            for (const page of pages) {
                // exclude home page from groups
                if (page?.homePage === true || page?.type === `blog`) {
                    continue;
                }
                const key = page.category && page.category.trim() ? page.category : `__NONE__`;
                const label = key === `__NONE__` ? this.$tr(`No Category|Aucune catégorie`) : key;
                if (!map.has(key)) {
                    map.set(key, { key, label, pages: [] });
                }
                map.get(key).pages.push(page);
            }

            let groups = Array.from(map.values());

            const statusOrder = {
                published: 0,
                draft: 1,
                archive: 2
            };

            for (const group of groups) {
                group.pages.sort((a, b) => {
                    const sa = statusOrder[a.status] ?? 99;
                    const sb = statusOrder[b.status] ?? 99;
                    if (sa !== sb) {
                        return sa - sb;
                    }
                    return (this.$tr(a.name) || ``).localeCompare(this.$tr(b.name) || ``);
                });
            }

            groups.sort((a, b) => {
                if (a.key === `__NONE__`) {
                    return 1;
                }
                if (b.key === `__NONE__`) {
                    return -1;
                }
                return (a.label || ``).localeCompare(b.label || ``);
            });

            const blogPages = this.cmsPages.filter(p => p?.type === `blog`);
            if (blogPages.length > 0) {
                groups.splice(0, 0, {
                    key: `blog`,
                    label: `Blog`,
                    pages: this.cmsPages.filter(p => p?.type === `blog`) || []
                });
            }

            return groups;
        },
        homePages() {
            const pages = this.cmsPages || [];
            return pages.filter(p => p && p.homePage === true) || [];
        },
        pageChecker() {
            return this.$store.state.assistant.pageChecker[this.displayLanguage];
        }
    },
    watch: {
        'cmsWebsite.settings.languages': {
            handler() {
                if (this.translationAssistant?.modalVisible) {
                    this.ensureTranslationAssistantTargets();
                }
            },
            deep: true
        },
        'translationAssistant.modalVisible'(value) {
            if (value) {
                this.ensureTranslationAssistantTargets();
            }
        }
    },
    async activated() {
        await this.init();
    },
    async mounted() {
        await this.init();

        // Reset state for kiosk preview
        this.$store.state.isKioskDevice = false;
        window.isKioskDevice = false;
        localStorage.removeItem(`kiosk`);
        window.addEventListener(`focus`, this.onWindowFocus);
    },
    beforeDestroy() {
        window.removeEventListener(`focus`, this.onWindowFocus);
    },
    methods: {
        changeLanguage() {
            changeLanguage();
            window.location.reload();
        },
        changePreviewLanguage(event) {
            const normalized = normalizeLocaleCode(event) || event;
            setLocale(normalized);
            this.$store.state.previewLanguage = normalized;
            this.updateReview(false);
        },
        initTranslationContext() {
            if (!this.cmsPage.meta.translationContext) {
                this.cmsPage.meta.translationContext = {};
            }

            if (!this.cmsPage.meta.translationContext.en || this.cmsPage.meta.translationContext.en === ``) {
                this.cmsPage.meta.translationContext.en = this.cmsPage.meta.description.en;
            }

            if (!this.cmsPage.meta.translationContext.fr || this.cmsPage.meta.translationContext.fr === ``) {
                this.cmsPage.meta.translationContext.fr = this.cmsPage.meta.description.fr;
            }
        },
        editWebsitePage(page) {
            this.$router.push(`/cms/editor/website/${this.cmsWebsite.id}/${page._id}`);
            window.location.reload();
        },
        async init() {
            if (this.loading) {
                return;
            }

            this.loading = true;
            this.showLoading();

            NetworkV2.get(`/api/page/${this.$route.params.pageId}`)
                .then(resp => {
                    this.$store.commit(`setPage`, { page: resp.data, route: this.$route });
                    // for future potential compatibility issues
                    if (window.env.VUE_APP_PACKAGE_VERSION < this.cmsPage.editorVersion) {
                        swal(this.$tr(`Warning|Attention`),
                            this.$tr(`This page was edited in a more recent version of the app|Cette page a été modifiée dans une version plus récente du logiciel`), `warning`);
                    }

                    if (!this.cmsWebsite.menu.block) {
                        // console.warn(`Tmp always removing previous header`)
                        let menu = new CmsBlockMenu();
                        menu.logo.src = this.cmsWebsite.design.logo.src;
                        const darkTheme = this.cmsWebsite.getColorScheme(`dark`);
                        if (darkTheme) {
                            menu.colorScheme = darkTheme;
                            menu.logo.filter = `recolor-white`;
                        }
                        this.$set(this.cmsWebsite.menu, `block`, menu);
                    }
                    if (!this.cmsWebsite.footer.block) {
                        // console.warn(`Tmp always removing previous footer`)
                        let footer = new CmsBlockFooter();
                        footer.logo.src = this.cmsWebsite.design.logo.src;
                        const darkTheme = this.cmsWebsite.getColorScheme(`dark`);
                        if (darkTheme) {
                            footer.colorScheme = darkTheme;
                            footer.logo.filter = `recolor-white`;
                        }
                        this.$set(this.cmsWebsite.footer, `block`, footer);
                    }
                    this.$forceUpdate();
                    this.hideLoading();
                    this.updateReview(false);
                    this.initTranslationContext();
                    this.loading = false;
                    this.fetchTemplates();
                })
                .catch(error => {
                    console.error(error);
                    this.loading = false;
                    this.hideLoading();
                })
        },
        setPreviewMode(previewMode) {
            this.previewMode = previewMode;
            this.$store.commit(`updateDisplayMode`);
            this.cmsWebsite.applyStyle();
        },
        setZoom(zoom) {
            this.$store.commit(`setZoom`, zoom);
        },
        setSimulatorMode(simulatorMode = `mobile`) {
            this.simulatorMode = simulatorMode;
            this.save(() => {
                this.simulatorVisible = !this.simulatorVisible;
            });
        },
        leaveSimulatorMode() {
            this.simulatorVisible = false;
            localStorage.removeItem(`kiosk`);
        },
        selectBlock(block) {
            if (this.mode === this.MODES.SEO)
                return;

            if (block) {
                if (this.blockSelected !== block) {
                    this.updateBlockSelection(block);
                    // document.getElementById(block.id)?.scrollIntoView();
                }
                this.mode = this.templateEditor.active ? this.MODES.TEMPLATE : this.MODES.BLOCK;
            } else {
                this.updateBlockSelection(null);
                this.mode = this.templateEditor.active ? this.MODES.TEMPLATE : this.MODES.STRUCT;
            }
        },
        scrollToBlock() {
            setTimeout(() => {
                this.blockSelected?.scrollTo();
            }, 150);
        },
        selectParentBlock() {
            this.selectBlock(this.blockSelectedParent);
        },
        unselectBlock() {
            if (this.blockSelected) {
                this.blockSelected.applyChanges(this.displayLanguage);
            }
            this.blockSelected = null;
            this.updateBlockSelection();
            this.resetMode();
        },
        updateBlockSelection(blockSelected) {
            if (!blockSelected) {
                this.blockSelection = [];
                return;
            }

            if (!this.templateEditor.active && [`menu`, `footer`].includes(blockSelected.type)) {
                this.blockSelection = [blockSelected];
                return;
            }

            let blockSelection = [];
            for (let block of this.cmsPage.blocks) {
                if (block.checkSelection(blockSelection, blockSelected)) {
                    break;
                }
            }
            this.blockSelection = blockSelection.reverse();
        },
        createBlock(preset) {
            const block = new preset();
            block.initNewBlock(this.cmsWebsite);
            if (this.blockSelected) {
                if (this.isBlockSelectedChild) {
                    this.blockSelectedParent.insertChild(block, this.blockIndex);
                } else {
                    this.cmsPage.insertBlock(block, this.blockIndex);
                }
            } else {
                this.cmsPage.addBlock(block);
            }
            this.$nextTick(() => {
                this.selectBlock(block);
                this.scrollToBlock();
            })
        },
        createBlockFromTemplate(template) {
            const BlockClass = window.cmsFactory.constructors.find(c => c.typeDef.type === template.block.type);
            if (!BlockClass) {
                this.showErrorMessage(`Error|Erreur`, `Template type not found|Type de modèle non trouvé`);
                return;
            }

            const block = new BlockClass(template.block);
            block.setPage(this.cmsPage);
            block.generateId();

            if (this.blockSelected) {
                if (this.isBlockSelectedChild) {
                    this.blockSelectedParent.insertChild(block, this.blockIndex);
                } else {
                    this.cmsPage.insertBlock(block, this.blockIndex);
                }
            } else {
                this.cmsPage.addBlock(block);
            }
            this.$nextTick(() => {
                this.selectBlock(block);
                this.scrollToBlock();
            })
        },
        toggleBlockContainer() {
            if (this.blockSelected.container === CmsBlock.CONTAINERS.CONTAINED) {
                this.blockSelected.container = CmsBlock.CONTAINERS.EXTENDED;
            } else if (this.blockSelected.container === CmsBlock.CONTAINERS.EXTENDED) {
                this.blockSelected.container = CmsBlock.CONTAINERS.FILL_WIDTH;
            } else {
                this.blockSelected.container = CmsBlock.CONTAINERS.CONTAINED;
            }
        },
        resetMode() {
            this.mode = this.templateEditor.active ? this.MODES.TEMPLATE : this.MODES.STRUCT;
        },
        moveBlockUp() {
            // this.cmsPage.swapBlock(this.blockSelected, -1);
            if (!this.blockSelected)
                return;

            const block = this.blockSelected;
            const parent = this.isBlockSelectedChild ? this.blockSelectedParent : null;
            const list = parent ? parent.children : this.cmsPage.blocks;
            const oldIndex = list.indexOf(block);

            if (oldIndex <= 0)
                return;

            const newIndex = oldIndex - 1;
            list.splice(oldIndex, 1);
            list.splice(newIndex, 0, block);

            this.addHistory({
                undo: () => {
                    list.splice(newIndex, 1);
                    list.splice(oldIndex, 0, block);
                },
                redo: () => {
                    list.splice(oldIndex, 1);
                    list.splice(newIndex, 0, block);
                }
            });
        },
        moveBlockDown() {
            // this.cmsPage.swapBlock(this.blockSelected, 1);
            if (!this.blockSelected)
                return;

            const block = this.blockSelected;
            const parent = this.isBlockSelectedChild ? this.blockSelectedParent : null;
            const list = parent ? parent.children : this.cmsPage.blocks;
            const oldIndex = list.indexOf(block);

            if (oldIndex < 0 || oldIndex === list.length - 1)
                return;

            const newIndex = oldIndex + 1;
            list.splice(oldIndex, 1);
            list.splice(newIndex, 0, block);

            this.addHistory({
                undo: () => {
                    list.splice(newIndex, 1);
                    list.splice(oldIndex, 0, block);
                },
                redo: () => {
                    list.splice(oldIndex, 1);
                    list.splice(newIndex, 0, block);
                }
            });
        },
        duplicateBlock() {
            if (!this.blockSelected)
                return;

            const parent = this.isBlockSelectedChild ? this.blockSelectedParent : null;
            const list = parent ? parent.children : this.cmsPage.blocks;
            const index = list.indexOf(this.blockSelected);

            let copy;
            if (this.isBlockSelectedChild) {
                copy = parent.duplicateChild(this.blockSelected);
            } else {
                copy = this.blockSelected.duplicate();
                this.cmsPage.insertBlock(copy, index);
            }

            this.$nextTick(() => {
                this.selectBlock(copy);
                this.scrollToBlock();
            });
            this.showToast(`Duplicated|Dupliqué`, 3000);

            this.addHistory({
                undo: () => {
                    if (parent) {
                        parent.deleteChild(copy);
                    } else {
                        this.cmsPage.deleteBlock(copy);
                    }
                },
                redo: () => {
                    if (parent) {
                        parent.children.splice(index + 1, 0, copy);
                    } else {
                        this.cmsPage.insertBlock(copy, index + 1);
                    }
                    this.$nextTick(() => this.selectBlock(copy));
                }
            });
        },
        openWrapBlockProperties() {
            if (!this.blockSelected) return;

            this.mode = this.MODES.WRAP;
        },
        wrapBlock(Preset) {
            if (!this.blockSelected) return;

            const wrapper = new Preset();
            wrapper.initNewBlock(this.cmsWebsite);

            this.blockSelected.container = CmsBlock.CONTAINERS.CONTAINED;

            const parent = this.blockSelectedParent;
            if (parent) {
                const index = parent.children.indexOf(this.blockSelected);
                parent.children.splice(index, 1, wrapper);
            } else {
                const index = this.cmsPage.blocks.indexOf(this.blockSelected);
                this.cmsPage.blocks.splice(index, 1, wrapper);
            }

            wrapper.addChild(this.blockSelected);

            this.mode = this.templateEditor.active ? this.MODES.TEMPLATE : this.MODES.BLOCK;
            this.$nextTick(() => this.selectBlock(wrapper));
        },
        replaceBlock(block) {
            if (this.blockSelectedParent) {
                this.$set(this.blockSelectedParent.children, this.blockIndex, block);
            } else {
                this.$set(this.cmsPage.blocks, this.blockIndex, block);
            }
            this.$nextTick(() => this.selectBlock(block));
        },
        async cutBlock() {
            if (!this.blockSelected)
                return;

            const clipboardItem = this.blockSelected.getClipboardItem(this.cmsPage, this.blockSelectedParent, `cut`);
            if (this.deleteBlock()) {
                await this.$store.dispatch(`clipboard/writeClipboard`, [clipboardItem]);
                this.showToast(`Section cut|Section coupée`, 2000);
            }
        },
        async copyBlock() {
            if (!this.blockSelected)
                return;

            const clipboardItem = this.blockSelected.getClipboardItem(this.cmsPage, this.blockSelectedParent, `copy`);
            await this.$store.dispatch(`clipboard/writeClipboard`, [clipboardItem]);

            this.showToast(`Section copy|Section copiée`, 2000);
        },
        async pasteBlockAsChild(evt) {
            await this.pasteBlock(evt, true);
        },
        async pasteBlock(evt, pasteAsChild = false) {
            if (document.activeElement && document.activeElement.className.indexOf(`text-editor`) > -1) {
                return;
            }

            const clipboardItem = this.$store.state.clipboard.item;

            if (!clipboardItem)
                return this.showToast(`Clipboard is empty|Le presse papier est vide`, 2000);

            let newBlock = null;
            if (clipboardItem.type === `block`) {
                const block = clipboardItem.block.duplicate();
                block.setPage(this.cmsPage);
                block.generateId();
                if (pasteAsChild === true || block.typeDef.requiresParent) {
                    if (this.blockSelected) {
                        if (this.blockSelected.typeDef.isParent && this.blockSelected.isChildValid(block)) {
                            newBlock = this.blockSelected.insertChild(block, this.blockIndex);
                        } else if (this.blockSelectedParent && this.blockSelectedParent.isChildValid(block)) {
                            newBlock = this.blockSelectedParent.insertChild(block, this.blockIndex);
                        }
                    }
                    if (!newBlock) {
                        swal(this.$tr(`Can't paste|Collage non disponible`), this.$tr(`Please select a container block in which to paste this item|Merci de sélectionner un block dans lequel coller cet élément`), `warning`);
                        return;
                    }
                } else {
                    if (this.blockSelected) {
                        newBlock = this.cmsPage.insertBlock(block, this.blockIndex);
                    } else {
                        newBlock = this.cmsPage.addBlock(block);
                    }
                }
            }

            if (newBlock) {
                this.$nextTick(() => this.selectBlock(newBlock));
                return this.showToast(`Block pasted|Bloc collé`, 2000);
            }

            //
            // this.addHistory({
            //     undo: () => {
            //         targetList.splice(targetIndex, 1);
            //         originalList.splice(originalIndex, 0, cutBlock);
            //     },
            //     redo: () => {
            //         originalList.splice(originalIndex, 1);
            //         targetList.splice(targetIndex, 0, wrapper);
            //     }
            // });
            //
            // originalList.splice(originalIndex, 1);
            // this.clipboard = null;
            // this.$nextTick(() => this.selectBlock(wrapper));
        },
        async pasteBlockStyle() {
            if (!this.blockSelected)
                return;

            if (document.activeElement && document.activeElement.className.indexOf(`text-editor`) > -1) {
                return;
            }

            await this.$store.dispatch(`clipboard/readClipboard`);
            const clipboardItem = this.$store.state.clipboard.item;

            if (!clipboardItem)
                return this.showToast(`Clipboard is empty|Le presse papier est vide`, 2000);

            if (clipboardItem.type === `block`) {
                const block = clipboardItem.block;

                if (this.blockSelected.type === clipboardItem.block.type) {
                    this.blockSelected.loadStyle(block.toJSON());
                } else {
                    const destinationName = this.$tr(this.blockSelected.typeDef.name);
                    const sourceName = this.$tr(block.typeDef.name);
                    swal(this.$tr(`Style not compatible|Style non compatible`),
                        this.$tr(`Can't apply style from ${sourceName} to ${destinationName}|Impossible d'appliquer le style de ${sourceName} à ${destinationName}`),
                        `warning`);
                    return;
                }
            }
        },
        deleteBlock() {
            if (!this.blockSelected)
                return false;

            const block = this.blockSelected;
            const parent = this.isBlockSelectedChild ? this.blockSelectedParent : null;
            const list = parent ? parent.children : this.cmsPage.blocks;
            const index = list.indexOf(block);

            if (this.isBlockSelectedChild) {
                if (block.childrenReadonly) {
                    this.showToast(`Can't delete this section|Cette section ne peut pas être supprimée`, 3000);
                    return false;
                }
                parent.deleteChild(block);
                this.selectBlock(parent);
            } else {
                this.cmsPage.deleteBlock(block);
                this.unselectBlock();
            }

            this.addHistory({
                undo: () => {
                    list.splice(index, 0, block);
                    this.$nextTick(() => this.selectBlock(block));
                },
                redo: () => {
                    if (parent) {
                        parent.deleteChild(block);
                    } else {
                        this.cmsPage.deleteBlock(block);
                    }
                }
            });
            return true;
        },
        addHistory(cmd) {
            this.history.splice(this.historyIndex + 1);
            this.history.push(cmd);
            this.historyIndex++;
        },
        undo() {
            if (this.historyIndex < 0) return;
            this.history[this.historyIndex].undo();
            this.historyIndex--;
            this.showToast(`Undo`, 500);
        },
        redo() {
            if (this.historyIndex + 1 >= this.history.length) return;
            this.historyIndex++;
            this.history[this.historyIndex].redo();
            this.showToast(`Redo`, 500);
        },
        showIframeDialog() {
            this.pageCreationVisible = true;
        },
        setAddBlockMode() {
            this.mode = this.MODES.ADD_BLOCK;
        },
        openTemplatesManager() {
            if (!this.templateSelectionModal.visible) {
                this.templateSelectionModal.visible = true;
            }
        },
        onSelectTemplateForEditing(template) {
            if (!template || !template.block) {
                return;
            }
            this.templateSelectionModal.visible = false;
            this.openTemplateEditor(template);
        },
        onPageEdited() {
            this.save(() => {
                this.$store.dispatch(`refreshWebsite`);
            })
        },
        duplicatePage() {
            this.pageDuplicateVisible = true;
        },
        deletePage() {
            this.cmsPage.deleted = true;
            this.save(() => {
                window.location.href = `${window.env.VUE_APP_URLS__EYEINTELLIGENCE}/#/cms/dashboard`;
            })
        },
        async save(callback) {
            try {
                if (this.isTemplateMode) {
                    this.showErrorMessage(`Cannot save in Template Mode|Impossible d'enregistrer en mode modèle`, `Please exit Template Mode before saving.|Veuillez quitter le mode modèle avant d'enregistrer.`);
                    return;
                }

                this.showLoading();

                // if (this.websiteEdited) {
                if (this.canWrite(Permission.CMS.feature.WEBSITES)) {
                    const websiteSave = this.cmsWebsite.toJSON();
                    await NetworkV2.post(`/api/websites/edit`, websiteSave);
                } else {
                    console.warn(`Not allowed to edit website settings`);
                }
                // }

                const pageSave = this.cmsPage.toJSON();
                await NetworkV2.post(`/api/pages/edit`, pageSave);
                this.hideLoading();
                this.showToast(`Page Saved|Page enregistrée`, undefined, `b-toaster-bottom-right`);
                if (callback && typeof callback === `function`) {
                    callback();
                }
            } catch (e) {
                console.error(e);
                this.showErrorMessage(`Not allowed|Non autorisé`, `You don't permission to do this action|Vous n'avez pas la permission de faire cette action`);
            }
        },
        publishPage() {
            this.cmsPage.status = `published`;
            this.cmsPage.datePublished = new Date();
        },
        draftPage() {
            this.cmsPage.status = `draft`;
        },
        pendingPage() {
            this.cmsPage.status = `pending`;
        },
        archivePage() {
            this.cmsPage.status = `archive`;
        },
        setWebsiteEdited() {
            this.websiteEdited = true;
            if (this.blockSelected) {
                this.mode = this.MODES.BLOCK;
            } else {
                this.mode = this.MODES.STRUCT;
            }
        },
        openWebsite() {
            swal({
                title: this.tr(`Save and Open|Ouvrir et quitter?`),
                text: this.tr(`We will save your modifications before leaving the page.|Nous allons enregistrer vos modifications avant de quitter cette page`),
                icon: `info`,
                buttons: {
                    cancel: this.tr(`Cancel|Annuler`),
                    dontSave: this.tr(`Don't save|Ne pas enregistrer`),
                    save: {
                        text: this.tr(`Save|Enregistrer`)
                    }
                }
            }).then(result => {
                if (result === `save`) {
                    this.save(() => {
                        this.$router.push(`/cms-admin`);
                    })
                } else if (result === `dontSave`) {
                    this.$router.push(`/cms-admin`);
                }
            });
        },
        websiteSettingsSaved() {
            this.showSavedMessage(`Saved|Enregistré`, `Website Settings have been updated|Les paramètres du site ont été modifiés`);
            window.location.reload();
        },
        exit() {
            this.showConfirmationMessage(`Save and Exit|Enregistrer et quitter?`, `We will save your modifications before returning to Eye-Intelligence.|Nous allons enregistrer vos modifications avant de retourner sur Eye-Intelligence`)
                .then(result => {
                    if (result) {
                        this.save(() => {
                            window.location.href = `${window.env.VUE_APP_URLS__EYEINTELLIGENCE}/#/cms/dashboard`;
                        })
                    }
                });
        },
        onPageCreated(page) {
            this.showSavedMessage(`Page Created|Page créé`)
            this.$store.dispatch(`refreshWebsite`);
            this.openPage(page);
        },
        onPageDuplicate(page) {
            this.showSavedMessage(`Page Duplicated|Page dupliquée`)
            this.$store.dispatch(`refreshWebsite`);
            this.openPage(page);
        },
        openPage(page) {
            this.unselectBlock();
            this.$router.push(`/cms/editor/website/${this.cmsWebsite.id}/${page.id}`);
            this.init();
        },
        toggleGroupedPagesSubmenu(groupKey) {
            if (this.groupedPagesSubmenuVisible === groupKey) {
                this.groupedPagesSubmenuVisible = null;
            } else {
                this.groupedPagesSubmenuVisible = groupKey;
            }
        },
        showGroupedPagesSubmenu(groupKey) {
            this.groupedPagesSubmenuVisible = groupKey;
        },
        hideGroupedPagesSubmenu(groupKey) {
            if (this.groupedPagesSubmenuVisible === groupKey) {
                this.groupedPagesSubmenuVisible = null;
            }
        },
        ensureTranslationAssistantTargets() {
            const languages = this.translationAssistantLanguageCodes;
            const fallback = languages[0] || getFallbackLocale();
            const from = normalizeLocaleCode(this.translationAssistant.fromLanguage) ||
                normalizeLocaleCode(this.displayLanguage) ||
                fallback;
            const safeFrom = languages.includes(from) ? from : fallback;
            this.translationAssistant.fromLanguage = safeFrom;

            const toCandidate = normalizeLocaleCode(this.translationAssistant.toLanguage);
            let safeTo = toCandidate && languages.includes(toCandidate) ? toCandidate : null;
            if (!safeTo || safeTo === safeFrom) {
                safeTo = languages.find(code => code !== safeFrom) || safeFrom;
            }
            this.translationAssistant.toLanguage = safeTo;
        },
        /*
        Texts and Translations
         */
        getLanguageEntry(map, language) {
            if (!map || typeof map !== `object`) {
                return null;
            }
            const normalized = normalizeLocaleCode(language) || language;
            if (normalized && map[normalized] !== undefined) {
                return map[normalized];
            }
            const fallbackKey = Object.keys(map).find(key => normalizeLocaleCode(key) === normalized);
            return fallbackKey ? map[fallbackKey] : null;
        },
        async autoTranslate(replaceAll = false) {
            const context = this.getLanguageEntry(this.cmsPage.meta.translationContext, this.translateLanguage);

            this.showLoading(`Translating...|Traduction en cours...`);

            try {
                if (this.translationAssistant.fullText) {
                    const sourcePageChecker = new PageChecker(this.cmsWebsite, this.cmsPage, this.translationAssistantFromLanguage);

                    this.showLoading();
                    const wordSpecs = this.getLanguageEntry(this.cmsPage.specs.words, this.translateLanguage) || {};
                    const resp = await NetworkV2.post(`/api/seo/assistant/page/translate`, {
                        topic: this.getLanguageEntry(this.cmsPage.hiddenMeta.topic, this.translateLanguage),
                        mainKeywords: this.getLanguageEntry(this.cmsPage.specs.mainKeywords, this.translateLanguage),
                        keywords: this.getLanguageEntry(this.cmsPage.specs.keywords, this.translateLanguage),
                        audience: this.getLanguageEntry(this.cmsPage.hiddenMeta.audience, this.translateLanguage),
                        targetMarket: this.getLanguageEntry(this.cmsPage.hiddenMeta.targetMarket, this.translateLanguage),
                        competitors: this.getLanguageEntry(this.cmsPage.hiddenMeta.competitors, this.translateLanguage),
                        content: sourcePageChecker.textContent,
                        words: this.getLanguageEntry(sourcePageChecker.stats?.words, this.translateLanguage),
                        minWords: wordSpecs?.min,
                        maxWords: wordSpecs?.max,
                        from: this.translationAssistantFromLanguage,
                        to: this.translateLanguage,
                        instructions: context
                    }, {
                        timeout: 600000
                    });

                    this.$store.state.assistant.ai.translation[this.translateLanguage] = resp.data;
                    this.$store.dispatch(`comparison/enableTextDiff`, {
                        suggestions: resp.data.content,
                        page: this.cmsPage,
                        type: `translation`,
                        language: this.translateLanguage
                    });
                    this.hideLoading();
                } else {
                    // translate block by block
                    await this.cmsPage.translate(this.translationAssistantFromLanguage, this.translateLanguage, replaceAll, context);
                }
                setLocale(this.translateLanguage);
                this.$store.state.previewLanguage = this.translateLanguage;
                this.updateReview(false);
                this.showSavedMessage(`Page Translated|Page traduite`);
                this.$store.commit(`updateCache`);
            } catch (err) {
                this.showErrorFeedback(err);
                console.error(err);
            }
            // this.$forceUpdate();
        },
        async resetLanguageTexts() {
            this.showConfirmationMessage(`Empty all Texts?|Effacer tous les textes?`,
                `This will remove all current texts. Use this after duplicating a page or to force translate everything again.|Cela supprimera tous les textes actuels. Utilisez cette fonction après avoir dupliqué une page, ou pour recommencer la traduction de la page de zéro.`,
                `Empty all Texts|Effacer tous les textes`)
                .then((value) => {
                    if (value) {
                        this.cmsPage.resetLanguageTexts(this.displayLanguage);
                        // next tick is not enough
                        setTimeout(() => {
                            this.$store.commit(`updateCache`);
                            this.$forceUpdate();
                        }, 150);
                    }
                });
        },
        async swapLanguageTexts() {
            for (let block of this.cmsPage.blocks) {
                block.swapLanguageTexts();
            }
            this.$store.state.previewLanguage = this.translateLanguage;
            this.$forceUpdate();
        },
        clearAllTextFormatting() {
            if (!this.cmsPage) return;

            const clearComponentFormatting = (component) => {
                if (component && component.type === `text` && component.text) {
                    // Clear formatting for each language
                    for (let lang in component.text) {
                        if (component.text[lang]) {
                            // Create a temporary element to extract plain text
                            const tmp = document.createElement(`div`);
                            tmp.innerHTML = component.text[lang];
                            component.text[lang] = (tmp.textContent || tmp.innerText || ``).replace(/\r\n/g, `\n`);
                        }
                    }
                    // Apply changes to sync the component
                    component.applyChanges();
                }
            };

            const traverseBlock = (block) => {
                if (!block) return;

                // Check all properties of the block for components
                for (let key in block) {
                    const prop = block[key];
                    if (prop && typeof prop === `object`) {
                        // Check if it's a text component
                        if (prop.type === `text`) {
                            clearComponentFormatting(prop);
                        }
                        // Recursively check for nested components and blocks
                        else if (prop.type && typeof prop.applyChanges === `function`) {
                            // It's another type of component, check if it has text properties
                            for (let subKey in prop) {
                                if (prop[subKey] && prop[subKey].type === `text`) {
                                    clearComponentFormatting(prop[subKey]);
                                }
                            }
                        }
                        // Handle arrays (like children blocks)
                        else if (Array.isArray(prop)) {
                            prop.forEach(item => {
                                if (item && typeof item === `object`) {
                                    traverseBlock(item);
                                }
                            });
                        }
                    }
                }

                // Traverse child blocks if they exist
                if (block.children && Array.isArray(block.children)) {
                    block.children.forEach(child => traverseBlock(child));
                }
            };

            // Traverse all blocks in the page
            if (this.cmsPage.blocks && Array.isArray(this.cmsPage.blocks)) {
                this.cmsPage.blocks.forEach(block => traverseBlock(block));
            }

            this.showToast(`All text formatting cleared|Tout le formatage du texte a été effacé`, 3000);

            this.$forceUpdate();
        },
        onWindowFocus(evt) {
            this.$store.dispatch(`clipboard/readClipboard`);
        },
        setModeSEO() {
            this.updateReview(false);
            this.mode = this.MODES.SEO;
        },
        updateReview(showToast = true) {
            this.$set(this.$store.state.assistant.pageChecker, this.displayLanguage, new PageChecker(this.cmsWebsite, this.cmsPage, this.displayLanguage));
            if (showToast) {
                this.showToast(`Review updated`);
            }
            this.$forceUpdate();
        },
        openPageSettings(tabIndex) {
            if (tabIndex >= 0) {
                this.pageSettings.tab = tabIndex;
            }
            this.pageSettings.modalVisible = true;
        },
        editBlockDetails() {
            if (!this.templateEditor.active || !this.templateEditor.template) {
                return;
            }
            this.templateEditor.editingTemplate = this.templateEditor.template;
            this.templateEditor.editTemplateName = this.templateEditor.template.name || ``;
            this.templateEditor.editTemplateDescription = this.templateEditor.template.description || ``;
            this.templateEditor.editDetailsModalVisible = true;
        },
        updateTemplateDetails() {
            if (!this.templateEditor.editTemplateName.trim()) {
                this.showToast(`Template name is required|Le nom du modèle est requis`, 3000, `error`);
                return;
            }

            const updatedTemplate = {
                ...this.templateEditor.editingTemplate,
                name: this.templateEditor.editTemplateName.trim(),
                description: this.templateEditor.editTemplateDescription.trim()
            };

            this.$emit(`update-template`, updatedTemplate);
            this.templateEditor.template = updatedTemplate;
            this.templateEditor.editDetailsModalVisible = false;
            this.templateEditor.editingTemplate = null;
        },
        openTemplateEditor(template) {
            if (!template || !template.block) {
                return;
            }
            const block = this.createTemplateBlockInstance(template.block);
            if (!block) {
                this.showToast(`Template type not found|Type de modA"le non trouvA?`, 3000, `error`);
                return;
            }
            this.templateSelectionModal.visible = false;
            this.templateEditor.previous = {
                mode: this.mode,
                blockSelected: this.blockSelected,
                blocks: this.cmsPage.blocks
            };
            this.templateEditor.active = true;
            this.templateEditor.template = template;
            this.$set(this.cmsPage, `blocks`, [block]);
            this.mode = this.MODES.TEMPLATE;
            this.blockSelection = [];
            this.selectBlock(block);
        },
        createTemplateBlockInstance(blockData) {
            if (!blockData || !blockData.type) {
                return null;
            }
            const data = JSON.parse(JSON.stringify(blockData));
            const constructor = window.cmsFactory?.constructors?.find(c => c.typeDef.type === data.type);
            if (!constructor) {
                return null;
            }
            const block = new constructor(data, this.cmsPage);
            if (typeof block.applyNewId === `function`) {
                block.applyNewId();
            } else if (typeof block.generateId === `function`) {
                block.generateId();
            }
            if (typeof block.setPage === `function`) {
                block.setPage(this.cmsPage);
            }
            return block;
        },
        cancelTemplateEditor() {
            this.exitTemplateEditor();
        },
        async saveTemplateEditor() {
            if (!this.templateEditor.active || !this.templateEditor.template || !this.cmsPage?.blocks?.length) {
                this.exitTemplateEditor();
                return;
            }
            const block = this.cmsPage.blocks[0];
            const updatedTemplate = {
                ...this.templateEditor.template,
                block: block.toJSON()
            };
            const success = await this.updateTemplate(updatedTemplate);
            if (!success) {
                return;
            }
            if (this.templateEditor.template) {
                this.templateEditor.template.block = updatedTemplate.block;
            }
        },
        exitTemplateEditor() {
            const previous = this.templateEditor.previous;
            const fallbackMode = previous && previous.mode ? previous.mode : this.MODES.STRUCT;

            if (previous && previous.blocks) {
                this.$set(this.cmsPage, `blocks`, previous.blocks);
            }

            this.templateEditor.active = false;
            this.templateEditor.template = null;
            this.templateEditor.previous = null;

            if (previous && previous.blockSelected) {
                this.selectBlock(previous.blockSelected);
                if (this.blockSelection.length && this.blockSelection[this.blockSelection.length - 1] === previous.blockSelected) {
                    this.mode = previous.mode || this.MODES.BLOCK;
                    return;
                }
            }

            this.blockSelection = [];
            this.mode = fallbackMode;
        },
        duplicateTemplateBlock() {
            if (!this.blockSelected || !this.templateEditor.template) {
                return;
            }

            const block = this.blockSelected;
            this.duplicateTemplateModal.blockToDuplicate = JSON.parse(JSON.stringify(block.toJSON()));
            this.duplicateTemplateModal.templateName = this.$tr(`${this.templateEditor.template.name} Copy|${this.templateEditor.template.name} Copie`);
            this.duplicateTemplateModal.templateDescription = this.templateEditor.template.description || ``;
            this.duplicateTemplateModal.visible = true;
        },
        openDuplicateTemplateModal(template) {
            if (!template) {
                return;
            }

            this.duplicateTemplateModal.blockToDuplicate = JSON.parse(JSON.stringify(template.block));
            this.duplicateTemplateModal.templateName = this.$tr(`${template.name} Copy|${template.name} Copie`);
            this.duplicateTemplateModal.templateDescription = template.description || ``;
            this.duplicateTemplateModal.visible = true;
        },
        insertTemplateBlock(template) {
            if (!template) {
                return;
            }

            this.createBlockFromTemplate(template);
            this.templateSelectionModal.visible = false;
            this.showToast(`Template inserted|Modèle inséré`, 3000);
        },
        saveDuplicateTemplateBlock() {
            if (!this.duplicateTemplateModal.templateName.trim()) {
                this.showToast(`Block name is required|Le nom du bloc est requis`, 3000, `error`);
                return;
            }

            if (!this.duplicateTemplateModal.blockToDuplicate) {
                return;
            }

            const originalPrevious = this.templateEditor.active ? this.templateEditor.previous : null;

            const templateBlockData = this.duplicateTemplateModal.blockToDuplicate;

            const templateData = {
                name: this.duplicateTemplateModal.templateName.trim(),
                description: this.duplicateTemplateModal.templateDescription.trim(),
                block: templateBlockData
            };

            this.showToast(`Block duplicated|Bloc dupliqué`, 3000);

            this.saveTemplate(templateData).then(async() => {
                this.duplicateTemplateModal.visible = false;
                this.duplicateTemplateModal.blockToDuplicate = null;
                this.duplicateTemplateModal.templateName = ``;
                this.duplicateTemplateModal.templateDescription = ``;

                await this.fetchTemplates();

                if (this.templateEditor.active && originalPrevious) {
                    const newTemplate = this.templates.find(t => t.name === templateData.name);
                    if (newTemplate) {
                        this.openTemplateEditor(newTemplate);
                        this.templateEditor.previous = originalPrevious;
                    }
                }
            });
        },
        async fetchTemplates() {
            try {
                const resp = await NetworkV2.get(`/api/templates/${this.cmsWebsite.id}`);
                const templates = resp.data || [];
                this.templates = templates.map(data => {
                    const normalized = { ...data };
                    if (!normalized.dealerid && this.cmsWebsite?.dealerid) {
                        normalized.dealerid = this.cmsWebsite.dealerid;
                    }
                    return new CmsBlockTemplate(normalized);
                });
                if (this.templateEditor.template) {
                    const updated = this.templates.find(t => t.id === this.templateEditor.template.id);
                    if (updated) {
                        this.templateEditor.template.name = updated.name;
                        this.templateEditor.template.description = updated.description;
                    }
                }
            } catch (err) {
                console.error(`Failed to fetch templates:`, err);
                this.templates = [];
            }
        },
        async saveTemplate(templateData) {
            try {
                const template = new CmsBlockTemplate({
                    websiteId: this.cmsWebsite.id,
                    dealerid: this.cmsWebsite.dealerid,
                    name: templateData.name,
                    description: templateData.description || ``,
                    block: templateData.block
                });

                if (!template.isValid()) {
                    this.showErrorMessage(`Error|Erreur`, `Invalid template data|Données de modèle invalides`);
                    return false;
                }

                this.showLoading();
                const resp = await NetworkV2.post(`/api/templates/save`, {
                    template: template.toJSON(),
                    websiteId: this.cmsWebsite.id,
                    dealerid: this.cmsWebsite.dealerid
                });
                this.showSavedMessage(`Template Saved|Modèle enregistré`);
                await this.fetchTemplates();
                return true;
            } catch (err) {
                console.error(`Failed to save template:`, err);
                this.showErrorMessage(`Error|Erreur`, `Failed to save template.|Échec de l'enregistrement du modèle.`);
                return false;
            }
        },
        async deleteTemplate(templateId) {
            try {
                if (!templateId) {
                    throw new Error(`Invalid template ID`);
                }

                this.showConfirmationMessage(`Delete Template Block?|Supprimer le bloc modèle?`, `This will also delete the template from your saved templates.|Cela supprimera également le modèle de vos modèles enregistrés.`, `Delete`)
                    .then(async(confirmed) => {
                        if (confirmed) {
                            const template = this.templates.find(t => t.id === templateId);
                            if (template) {
                                template.softDelete();
                            }
                            this.showLoading();
                            await NetworkV2.delete(`/api/templates/${templateId}`);
                            await this.fetchTemplates();
                            this.showSavedMessage(`Template Deleted|Modèle supprimé`);
                            this.exitTemplateEditor();
                        }
                    });
            } catch (err) {
                console.error(`Failed to delete template:`, err);
                this.showErrorMessage(`Error|Erreur`, `Failed to delete template.|Échec de la suppression du modèle.`);
            }
        },
        async updateTemplate(templateData) {
            try {
                const normalizedTemplateData = {
                    ...templateData,
                    dealerid: templateData.dealerid || this.cmsWebsite.dealerid
                };
                const template = new CmsBlockTemplate(normalizedTemplateData);
                template.updateMetadata(templateData.name, templateData.description || ``);
                template.updateBlock(templateData.block);

                if (!template.isValid()) {
                    this.showErrorMessage(`Error|Erreur`, `Invalid template data|Données de modèle invalides`);
                    return false;
                }

                this.showLoading();
                const resp = await NetworkV2.put(`/api/templates/${templateData.id}`, { template: template.toJSON() });
                this.showSavedMessage(`Template Updated|Modèle mis à jour`);
                await this.fetchTemplates();
                return true;
            } catch (err) {
                console.error(`Failed to update template:`, err);
                this.showErrorMessage(`Error|Erreur`, `Failed to update template.|Échec de la mise à jour du modèle.`);
                return false;
            }
        },
        openPreview() {
            window.open(this.previewUrl, `_blank`);
        }
    }
}
</script>

<style lang="scss">
@import "../assets/style/editor/editor";

.web-editor {
    --color-accent: #005fe5;
    height: 100vh;
    overflow: hidden;

    .editor-menu {
        border-bottom: 1px solid #ddd;
        padding: 4px 4px;
        height: 44px;

        .nav {
            align-items: center;
        }

        .e-page-dropdown {
            & > button {
                max-width: 300px;
                overflow: hidden;
                text-overflow: ellipsis;
            }


            .dropdown-menu {
                overflow: visible;
            }

            .dropdown-submenu {
                position: relative;
            }

            .dropdown-submenu-toggle {
                display: flex;
                align-items: center;
                width: 100%;
            }

            .dropdown-submenu-menu {
                position: absolute;
                top: 0;
                left: 100%;
                display: none;
                min-width: 240px;
                padding: 0.25rem 0;
                background: #fff;
                border: 1px solid rgba(0, 0, 0, 0.15);
                border-radius: 0.25rem;
                box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15);
                z-index: 1070;
            }

            .dropdown-submenu-menu.is-open {
                display: block;
            }

            .dropdown-submenu-menu .dropdown-item {
                display: flex;
                align-items: center;
            }

            .dropdown-submenu-menu-item-label {
                max-width: 200px;
                text-overflow: ellipsis;
                overflow: hidden;
                white-space: nowrap;
            }
        }
    }

    .preview-responsive-container {
        width: 100%;
        height: 100%;
        min-height: auto !important;
        display: flex;
        flex-direction: column;

        .overflow {
            width: 100%;
            height: 100%;
            overflow: hidden auto;
            scroll-behavior: smooth;
        }
    }
}

.responsive-preview.view-desktop iframe {
    height: 100% !important;
    max-height: 100% !important;
}

.editor-container {
    display: flex;
    height: 100%;
    width: 100%;
    background-color: #bbb;
    overflow: hidden;

    &.vertical {
        flex-direction: column;
    }
}

.padding-bar {
    position: sticky;
    background: var(--color-light);
    z-index: 1;

    .toggle-advanced-padding {
        position: relative;
        margin: 1px 0;
        width: 20px;
        height: 26px;
        display: flex;
        align-items: center;
        justify-content: center;
    }

    &.horizontal {
        top: 0;
        border-bottom: 1px solid #ccc;
        height: 26px;

        //&:after {
        //    position: absolute;
        //    content: "";
        //    display: block;
        //    left: 0;
        //    right: 0;
        //    border-bottom: 1px solid #ccc;
        //}

        .cms-container {
            display: flex;
            height: 26px;
            align-items: center;
        }

        .toggle-container {
            width: 30px;
            opacity: 0.5;

            &.fa-chevron-left,
            &.fa-chevrons-left {
                margin-left: -33px;
            }

            &:hover {
                opacity: 1;
            }
        }
    }

    &.vertical {
        width: 21px;
        border-right: 1px solid #ccc;
        display: flex;
        flex-direction: column;
        padding-top: 0;
        justify-content: center;
    }
}

.template-block-editor-container {
    display: flex;
    flex-direction: column;
    height: 100%;
    width: calc(100% - 380px);
    background: white;
}

.background-blueprint {
    position: relative;
    z-index: 0;
    background: none;
}

.background-blueprint::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-image: url('https://eyeintelligence.com/style/backgrounds/blueprint.jpg') !important;
    background-size: auto;
    background-repeat: repeat;
    filter: invert(1) grayscale(1) brightness(1.5) contrast(0.8);
}

.template-selection-container {
    max-width: 1200px;
    margin: 0 auto;
    overflow-y: auto;
    height: 100%;
}

.template-selection-header {
    background: rgba(255, 255, 255, 0.95);
    padding: 1.5rem;
    border-radius: 8px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);

    h3 {
        font-size: 1.5rem;
        font-weight: 600;
    }
}

.template-selection-modal-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
    gap: 1.5rem;

    .template-item-actions {
        position: absolute;
        top: 0;
        right: 0;
    }

    .dropdown .dropdown-item {
        min-width: auto !important;
    }
}

.template-action-btn {
    position: absolute;
    top: 8px;
    opacity: 0;
    transition: all 0.2s ease;
    background-color: rgba(255, 255, 255, 0.95);
    z-index: 10;
}

.template-delete-btn {
    @extend .template-action-btn;
    right: 8px;
    border-color: #dc3545;

    &:hover {
        background-color: #dc3545;
        color: white;
    }
}

.template-insert-btn {
    @extend .template-action-btn;
    right: 82px;
    border-color: #28a745;

    &:hover {
        background-color: #28a745;
        color: white;
    }
}

.template-duplicate-btn {
    @extend .template-action-btn;
    right: 45px;
    border-color: #0066cc;

    &:hover {
        background-color: #0066cc;
        color: white;
    }
}

.template-item-wrapper:hover {

    .template-delete-btn,
    .template-insert-btn,
    .template-duplicate-btn {
        opacity: 1;
    }
}

.template-large-preview {
    display: grid;
    place-items: center;
    flex-shrink: 0;
    width: 100%;
    max-width: 600px;
    min-height: 368px;
    overflow: hidden;
    border: 1px solid var(--border-color);
    border-radius: 4px;
    background: white;
    padding: 1rem;
}

@keyframes expandingOutline {
    0% {
        width: 100%;
        height: 100%;
        opacity: 1;
    }

    100% {
        width: 160%;
        height: 160%;
        opacity: 0;
    }
}

@keyframes progressStroke {
    0% {
        stroke-dashoffset: 100;
    }

    100% {
        stroke-dashoffset: 0;
    }
}

@keyframes xboxPopIn {
    0% {
        opacity: 0;
    }

    50% {
        opacity: 0.8;
    }

    95% {
        opacity: 1;
    }

    100% {
        opacity: 0;
    }
}

@keyframes toastGrow {
    0% {
        width: 0;
        height: 0;
    }

    100% {
        width: 200px;
        height: 60px;
    }
}

.template-mode-toast {
    position: absolute;
    bottom: 2rem;
    left: 50%;
    transform: translateX(-50%);
    z-index: 100;
    opacity: 0;

    animation: xboxPopIn 4.4s cubic-bezier(0.34, 1.56, 0.64, 1) 0.5s forwards;
}

.template-mode-toast .toast-box {
    position: relative;
    width: 200px;
    height: 60px;
    border-radius: 30px;
    overflow: hidden;
    animation: toastGrow 0.35s cubic-bezier(0.34, 1.56, 0.64, 1) 0.5s both;
}

.template-mode-toast svg {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    pointer-events: none;
    z-index: 1000;
}

.template-mode-toast svg rect {
    stroke: rgba(37, 99, 235, 0.8);
    stroke-width: 3;
    fill: none;
    stroke-linecap: round;
    stroke-dasharray: 100;
    stroke-dashoffset: 100;

    animation: progressStroke 4.4s linear 0.5s forwards;
}

.template-mode-toast .toast-content {
    position: relative;
    z-index: 2;
    box-sizing: border-box;
    width: 100%;
    height: 100%;

    display: flex;
    align-items: center;
    justify-content: center;
    gap: 14px;
    padding: 0 20px;

    background: linear-gradient(90deg, #F9FAFBFF 0%, var(--color-white) 50%, #EFF6FFFF 100%);
    color: #333333;
    border-radius: 30px;
    font-weight: 700;
    font-size: 15px;
    letter-spacing: 1px;
    white-space: nowrap;
    backdrop-filter: blur(30px);
    background-clip: padding-box;
    border: 1.5px solid rgba(37, 99, 235, 0.7);
}

.template-mode-toast .toast-content i {
    font-size: 20px;
    background: linear-gradient(135deg, #ffffff 0%, #e0f2ff 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    animation: pulse 2s ease-in-out infinite, float 3s ease-in-out infinite;
    filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.15));
}

@keyframes pulse {

    0%,
    100% {
        opacity: 1;
    }

    50% {
        opacity: 0.5;
    }
}

@keyframes float {

    0%,
    100% {
        transform: translateY(0px);
    }

    50% {
        transform: translateY(-2px);
    }
}
</style>
